// src/DashboardsApp.tsx

import React from "react";
import { PeriodCalendar } from "./PeriodCalendar";
import { QC_COLORS, QC_STATUS_LABEL } from "./quality/qc";
import type { QcCardRow } from "./quality/qc";
import { createPortal } from "react-dom";
import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { buildQualitySlides } from "./quality/QualityWidgets";
import { DeptSpecialtySlide, MkbReportSlide, ServiceKindsSlide, SoiProviderSlide, SoiTableSlide } from "./AppReportSlides";
import DashboardsTestPage from "./DashboardsTestPage";



class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorText: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, errorText: "" };
  }

  static getDerivedStateFromError(err: any) {
    return {
      hasError: true,
      errorText: String(err?.stack || err?.message || err),
    };
  }

  componentDidCatch(err: any) {
    // на всякий случай
    // eslint-disable-next-line no-console
    console.error("DashboardsApp crashed:", err);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 16, fontFamily: "monospace", whiteSpace: "pre-wrap", color: "#b00020" }}>
          <h2 style={{ margin: "0 0 10px 0" }}>Ошибка в DashboardsApp</h2>
          {this.state.errorText}
        </div>
      );
    }
    return this.props.children as any;
  }
}

type AuthPayload = {
  access_token?: string;
  iin?: string;
};

type OrgOption = {
  id: string;
  label: string;
};

type Filters = {
  mis_id?: string;
  finance_source_ids?: number[];
  year?: number;
  month?: number;
  day?: number;
  date_from?: string;
  date_to?: string;
  soi_id?: number;
  treatment_reason?: { code: number; purpose: number } | null;
  treatment_reason_label?: string;
  department_id?: number;
  performer_employee?: string;
  performer_employee_name?: string;
  department_id_10?: number;
  performer_employee_13?: string;
  performer_employee_13_name?: string;
  mkb10_id?: number;
  mkb10?: string;
  month_d?: number;
  day_d?: number;
  month_p?: number;
  day_p?: number;
};

type PendingTablePayload = {
  tableKey: string;
  entity_name: string;
  extra: Record<string, string | number | undefined | null>;
  title: string;
};

type DimItem = { id: number; name?: string; code?: string };

type AggYearFundingRow = {
  year: number;
  finance_source_id: number;
  finance_source_name?: string;
  total_count: number;
};
type AggMonthRow = { month: number; finance_source_id: number; finance_source_name?: string; total_count: number };
type AggDayRow = { date: string; finance_source_id: number; finance_source_name?: string; total_count: number };
type AggBarRow = { id: string; name: string; total_count: number };
type AggSeriesResponse = {
  buckets: number[];
  series: Array<{ id: string | number; name: string; values: number[] }>;
};


function decodeJwtPayload(token: string): any | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    const jsonStr = atob(b64 + pad);
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}

function getAuthPayload(): AuthPayload | null {
  try {
    const raw = localStorage.getItem("auth_payload");
    if (!raw) return null;

    const parsed: AuthPayload = JSON.parse(raw);

    // В некоторых сборках фронта iin не сохранялся в auth_payload.
    // Достаём iin из JWT и сохраняем обратно, чтобы все запросы стабильно
    // отправляли X-User-IIN (это критично для определения роли и прав).
    if (!parsed.iin && parsed.access_token) {
      const jwt = decodeJwtPayload(parsed.access_token);
      const iin = jwt?.iin ?? jwt?.sub;
      if (typeof iin === "string" && iin.length > 0) {
        parsed.iin = iin;
        try {
          localStorage.setItem("auth_payload", JSON.stringify(parsed));
        } catch {
          // ignore
        }
      }
    }

    return parsed;
  } catch {
    return null;
  }
}

type RoleResponse = { role?: string };

function isDashboardsRoleAllowed(role: string | undefined | null) {
  return role === "DIRECTOR" || role === "SUPERADMIN";
}

async function fetchUserRoleFromDefektolog(): Promise<string> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers["Authorization"] = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  // ВАЖНО: это НЕ dashboards-api, это defektolog backend
  const res = await fetch(`/api/v1/analytics/role`, { headers, credentials: "omit" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text && text.length < 500 ? text : `${res.status} ${res.statusText}`);
  }
  const data = (await res.json()) as RoleResponse;
  return String(data?.role || "").trim();
}

async function fetchUserOrgsFromDefektolog(): Promise<OrgOption[]> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers["Authorization"] = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  const res = await fetch(`/api/v1/analytics/dicts`, { headers, credentials: "omit" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text && text.length < 500 ? text : `${res.status} ${res.statusText}`);
  }
  const data = (await res.json()) as { orgs?: Array<Record<string, any>> };
  const orgs = Array.isArray(data?.orgs) ? data.orgs : [];
  return orgs
    .map((org) => {
      const id = String(org?.orgId || "").trim();
      if (!id) return null;
      const label = String(org?.name || org?.orgName || org?.shortName || id).trim();
      return { id, label: label || id };
    })
    .filter((org): org is OrgOption => Boolean(org?.id));
}

const DASHBOARDS_FETCH_CACHE_TTL_MS = 5 * 60 * 1000;
const dashboardsFetchCache = new Map<string, { ts: number; data: any }>();
const dashboardsFetchInflight = new Map<string, Promise<any>>();

async function dashboardsFetch<T>(
  path: string,
  opts: RequestInit = {},
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { ...(opts.headers as any), ...extraHeaders };

  if (auth?.access_token) headers["Authorization"] = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;
  // ✅ dashboards-backend авторизуется по X-Role (DIRECTOR/SUPERADMIN)
  const storedRole = (localStorage.getItem("dashboards_role") || "").trim();
  if (storedRole) headers["X-Role"] = storedRole;

  // Важно: все запросы к dashboards backend идут через nginx location /dashboards-api/
  const url = `/dashboards-api${path.startsWith("/") ? path : `/${path}`}`;

  const method = String(opts.method || "GET").toUpperCase();
  const canCache = method === "GET" && opts.body === undefined;
  const cacheKey = url;

  if (canCache) {
    const cached = dashboardsFetchCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < DASHBOARDS_FETCH_CACHE_TTL_MS) {
      return cached.data as T;
    }
    const inflight = dashboardsFetchInflight.get(cacheKey);
    if (inflight) return (await inflight) as T;
  }

  const fetchPromise = (async () => {
    const res = await fetch(url, { ...opts, headers, credentials: "omit" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const msg = text && text.length < 500 ? text : `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }
    const data = (await res.json()) as T;
    if (canCache) dashboardsFetchCache.set(cacheKey, { ts: Date.now(), data });
    return data;
  })();

  if (canCache) dashboardsFetchInflight.set(cacheKey, fetchPromise);
  try {
    return (await fetchPromise) as T;
  } finally {
    if (canCache) dashboardsFetchInflight.delete(cacheKey);
  }
}


function buildQueryFromFilters(filters: Filters, extra: Record<string, string | number | undefined | null> = {}) {
  const qs = new URLSearchParams();

  const set = (k: string, v: any) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  };

  const addList = (k: string, list?: number[]) => {
    if (!list?.length) return;
    list.forEach((item) => {
      if (Number.isFinite(item)) qs.append(k, String(item));
    });
  };

  set("mis_id", filters.mis_id);
  set("year", filters.year);
  set("month", filters.month);
  set("day", filters.day);
  set("date_from", filters.date_from);
  set("date_to", filters.date_to);
  addList("finance_source_id", filters.finance_source_ids);

  set("department_id", filters.department_id);
  set("performer_employee", filters.performer_employee);
  set("soi_id", filters.soi_id);
  set("mkb10_id", filters.mkb10_id);

  if (filters.treatment_reason?.code !== undefined) set("tr_code", filters.treatment_reason.code);
  if (filters.treatment_reason?.purpose !== undefined) set("tr_purpose", filters.treatment_reason.purpose);

  Object.entries(extra).forEach(([k, v]) => set(k, v));
  return qs;
}

function buildAggQuery(filters: Filters, overrides: Partial<Filters> = {}, extra: Record<string, string | number | undefined | null> = {}) {
  const merged = { ...filters, ...overrides };
  return buildQueryFromFilters(merged, extra);
}

/*function getIsoWeekInfo(date: Date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { isoYear: d.getUTCFullYear(), isoWeek: week };
}
*/
/*function resolveIsoWeekFromFilters(filters: Filters) {
  const now = new Date();
  const year = filters.year ?? now.getUTCFullYear();
  const month = Number.isFinite(filters.month) ? (filters.month as number) - 1 : 0;
  const day = Number.isFinite(filters.day) ? (filters.day as number) : 1;
  const base = new Date(Date.UTC(year, Math.max(0, month), Math.max(1, day)));
  return getIsoWeekInfo(base);
}
*/
/*function buildTableQuery(filters: Filters, extra: Record<string, string | number | undefined | null> = {}) {
  const qs = new URLSearchParams();

  const set = (k: string, v: any) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  };

  const addList = (k: string, list?: number[]) => {
    if (!list?.length) return;
    list.forEach((item) => {
      if (Number.isFinite(item)) qs.append(k, String(item));
    });
  };

  const { isoYear, isoWeek } = resolveIsoWeekFromFilters(filters);
  set("mis_id", filters.mis_id);
  set("iso_year", extra.iso_year ?? isoYear);
  set("iso_week", extra.iso_week ?? isoWeek);
  addList("finance_source_id", filters.finance_source_ids);
  set("department_id", filters.department_id);
  set("performer_employee", filters.performer_employee);
  set("soi_id", filters.soi_id);
  if (filters.treatment_reason?.code !== undefined) set("tr_code", filters.treatment_reason.code);
  if (filters.treatment_reason?.purpose !== undefined) set("tr_purpose", filters.treatment_reason.purpose);
  if (filters.mkb10) set("mkb10", filters.mkb10);

  Object.entries(extra).forEach(([k, v]) => set(k, v));
  return qs;
}
*/
function openTableByKeyInNewTab(
  tableKey: string,
  filters: Filters,
  extra: Record<string, string | number | undefined | null> = {}
) {
  const qs = new URLSearchParams();
  qs.set("table", tableKey);

  if (filters.mis_id) qs.set("mis_id", String(filters.mis_id));

  // finance_source_id — всегда списком (как реально шлёт фронт)
  if (filters.finance_source_ids?.length) {
    filters.finance_source_ids.forEach((id) => {
      if (Number.isFinite(id)) qs.append("finance_source_id", String(id));
    });
  }

  // ВАЖНО: сюда кладём только table-specific параметры (soi_id/date_from/date_to/и т.д.)
  Object.entries(extra).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  });

  const url = `/dashboards/app/table?${qs.toString()}`;
  window.open(url, "_blank", "noopener,noreferrer");
}



/*function openTableInNewTab(filters: Filters, extra: Record<string, string | number | undefined | null> = {}) {
  if (!filters.mis_id) {
    window.alert("Укажите mis_id, чтобы открыть таблицу.");
    return;
  }
  const qs = buildTableQuery(filters, extra);
  const url = `/dashboards/app/table?${qs.toString()}`;
  window.open(url, "_blank", "noopener,noreferrer");
}
*/
function formatMonthLabel(m: number) {
  return formatMonthGenitive(m);
}

const MONTHS_RU = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const MONTHS_RU_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

function formatMonthName(m: number) {
  const idx = Number(m) - 1;
  return MONTHS_RU[idx] || String(m);
}

function formatMonthGenitive(m: number) {
  const idx = Number(m) - 1;
  return MONTHS_RU_GENITIVE[idx] || String(m);
}

const MONTHS_RU_LOCATIVE = [
  "январе",
  "феврале",
  "марте",
  "апреле",
  "мае",
  "июне",
  "июле",
  "августе",
  "сентябре",
  "октябре",
  "ноябре",
  "декабре",
];

function formatMonthLocative(m: number) {
  const idx = Number(m) - 1;
  return MONTHS_RU_LOCATIVE[idx] || String(m);
}

function formatDayLabel(d: number) {
  return String(d).padStart(2, "0");
}


/*function toDateYmd(year: number, month: number, day: number) {
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}
*/
function getDaysInMonth(year: number, month: number) {
  const safeYear = Number.isFinite(year) ? year : new Date().getUTCFullYear();
  const safeMonth = Number.isFinite(month) ? month : 1;
  return new Date(Date.UTC(safeYear, safeMonth, 0)).getUTCDate();
}
function financeColorByLabel(label: string): string | undefined {
  const s = String(label || "").toLowerCase();
  const hasOsms = s.includes("осмс");
  const hasGobmp = s.includes("гобмп");
  if (hasOsms && !hasGobmp) return "#48B1CC";
  if (hasGobmp && !hasOsms) return "#eee36d";
  if (s.includes("неопредел")) return "#9AA0A6"; // НеОпределен
  if (s.includes("пдн")) return "#7E57C2";       // ПдН
  if (s === "пу" || s.includes(" пу")) return "#992222"; // ПУ
  return undefined;
}

const DEFAULT_ECHARTS_TOOLTIP = {
  backgroundColor: "#083264",
  borderColor: "rgba(255,255,255,0.3)",
  borderWidth: 1,
  textStyle: { color: "#fff" },
  extraCssText: "border-radius:10px;",
};

function resolveFinanceSourceLabel(filters: Filters, financeMap: Map<number, string>) {
  const ids = filters.finance_source_ids || [];
  if (!ids.length) return "всем источникам финансирования";
  if (ids.length === 1) return financeMap.get(ids[0]) || String(ids[0]);
  return ids.map((id) => financeMap.get(id) || String(id)).join(", ");
}

function resolveTreatmentReasonLabel(filters: Filters) {
  // ✅ если уже есть красивый label (из Сл6) — используем его
  const lbl = String(filters.treatment_reason_label ?? "").trim();
  if (lbl) return lbl;

  // fallback (если фильтр задан не через клик Сл6, а, например, из URL)
  if (filters.treatment_reason?.code === undefined || filters.treatment_reason?.purpose === undefined) {
    return "TreatmentReasonID";
  }
  return `${filters.treatment_reason.code}/${filters.treatment_reason.purpose}`;
}

function resolveDepartmentLabel(id: number | undefined, deptMap: Map<number, string>) {
  if (id === undefined) return "Отделение_10";
  return deptMap.get(id) || String(id);
}

function resolvePerformerLabel(filters: Filters) {
  const fio = String(filters.performer_employee_13_name ?? "").trim();
  if (fio) return fio;

  const id = filters.performer_employee_13;
  return !id ? "Медработник" : String(id);
}

function resolveFinanceColor(filters: Filters, financeMap: Map<number, string>) {
  // ✅ когда выбран MIS (или MIS + год) без источников финансирования — показываем “все ИФ” цветом #DC143C 
  if (!!filters.mis_id && !filters.finance_source_ids?.length) return "#DC143C";

  // ✅ если не выбран источник — по умолчанию ГОБМП (старое поведение для режимов без MIS)
  if (!filters.finance_source_ids?.length) return financeColorByLabel("ГОБМП") || "#ffee0f";

  const lbl = resolveFinanceSourceLabel(filters, financeMap);
  return financeColorByLabel(lbl);
}
function collapseAggRows(rows: AggBarRow[]): AggBarRow[] {
  const m = new Map<string, AggBarRow>();

  for (const r of rows || []) {
    const id = String(r.id);
    const prev = m.get(id);
    if (!prev) {
      m.set(id, { ...r, total_count: Number(r.total_count || 0) });
    } else {
      prev.total_count = Number(prev.total_count || 0) + Number(r.total_count || 0);
    }
  }

  return Array.from(m.values());
}

const PAGE_LAYOUTS = [1, 2, 3, 4, 6] as const;
type PageLayout = (typeof PAGE_LAYOUTS)[number];
const LAYOUT_STORAGE_KEY = "dashboards_app_layout_by_page";
const DEFAULT_LAYOUT: PageLayout = 2;

type SlidePresentationContextValue = {
  activeSlideId: string | null;
  setActiveSlideId: (slideId: string | null) => void;
};

const SlidePresentationContext = React.createContext<SlidePresentationContextValue>({
  activeSlideId: null,
  setActiveSlideId: () => undefined,
});

const LayoutContext = React.createContext<PageLayout>(DEFAULT_LAYOUT);


type SlideItem = {
  id: string;
  element: React.ReactNode;
};

function isValidLayout(value: unknown): value is PageLayout {
  return value === 1 || value === 2 || value === 3 || value === 4 || value === 6;
}

function readLayoutByPageFromStorage(): Record<number, PageLayout> {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const next: Record<number, PageLayout> = {};
    Object.entries(parsed || {}).forEach(([k, v]) => {
      const idx = Number(k);
      if (!Number.isInteger(idx) || idx < 0 || !isValidLayout(v)) return;
      next[idx] = v;
    });
    return next;
  } catch {
    return {};
  }
}

function writeLayoutByPageToStorage(layoutByPage: Record<number, PageLayout>) {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layoutByPage));
  } catch {
    // ignore
  }
}

function chunkSlides<T>(slides: T[], layoutByPage: Record<number, PageLayout>) {
  const pages: T[][] = [];
  let cursor = 0;
  let page = 0;

  while (cursor < slides.length) {
    const capacity = layoutByPage[page] ?? DEFAULT_LAYOUT;
    pages.push(slides.slice(cursor, cursor + capacity));
    cursor += capacity;
    page += 1;
  }

  return pages;
}

function MisIdDropdown({
  value,
  options,
  onChange,
}: {
  value: string;
  options: OrgOption[];
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((x) => x.id === value);

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 240, maxWidth: 320 }}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        style={{
          width: "100%",
          height: 36,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.3)",
          background: "#083264",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 10px",
          cursor: "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedOption?.label || "— выбрать —"}
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: "auto",
            background: "#083264",
            border: "1px solid rgba(255,255,255,0.28)",
            borderRadius: 10,
            zIndex: 100,
          }}
        >
          {[{ id: "", label: "— выбрать —" }, ...options].map((org) => (
            <div
              key={`mis-${org.id || "empty"}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                onChange(org.id);
              }}
              style={{
                padding: "8px 10px",
                color: "#fff",
                cursor: "pointer",
                fontWeight: value === org.id ? 700 : 500,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.fontWeight = "700";
                e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.fontWeight = value === org.id ? "700" : "500";
                e.currentTarget.style.background = "transparent";
              }}
            >
              {org.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FiltersDropdown({
  chips,
  onRemove,
}: {
  chips: Array<{ key: keyof Filters; label: string }>;
  onRemove: (key: keyof Filters) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        minWidth: 240,
        width: 300
      }}
    > <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        style={{
          width: "100%",
          height: 36,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.3)",
          background: "#083264",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 10px",
          cursor: "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Фильтры
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>

      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            maxHeight: 320,
            overflowY: "auto",
            background: "#083264",
            border: "1px solid rgba(255,255,255,0.28)",
            borderRadius: 10,
            zIndex: 120,
          }}
        >
          {!chips.length ? (
            <div style={{ padding: "10px 10px", color: "#fff", opacity: 0.9 }}>
              Активных фильтров нет.
            </div>
          ) : (
            chips.map((c) => (
              <div
                key={`chip-${String(c.key)}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  color: "#fff",
                }}
              >
                <button
                  type="button"
                  title="Убрать фильтр"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(c.key);
                  }}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(0,0,0,0.12)",
                    color: "#fff",
                    cursor: "pointer",
                    lineHeight: "20px",
                    fontWeight: 800,
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  ×
                </button>
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.label}
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}



function getGridTemplate(layout: PageLayout) {
  if (layout === 1) return { gridTemplateColumns: "1fr", gridTemplateRows: "1fr" };
  if (layout === 2) return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr" };
  if (layout === 3) return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" };
  if (layout === 4) return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" };
  return { gridTemplateColumns: "repeat(3, 1fr)", gridTemplateRows: "1fr 1fr" };
}

function renderPageCells(slides: SlideItem[], layout: PageLayout) {
  const cellStyle: React.CSSProperties = {
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  if (layout === 3 && slides.length >= 3) {
    return [
      <div key={slides[0].id} style={cellStyle}>{slides[0].element}</div>,
      <div key={slides[1].id} style={cellStyle}>{slides[1].element}</div>,
      <div
        key={slides[2].id}
        style={{ ...cellStyle, gridColumn: "1 / span 2" }}
      >
        {slides[2].element}
      </div>,
    ];
  }

  const cells = slides.map((slide) => (
    <div key={slide.id} style={cellStyle}>
      {slide.element}
    </div>
  ));

  if (layout === 6 && slides.length === 5) {
    cells.push(
      <div
        key="placeholder-6"
        style={{
          ...cellStyle,
          border: "1px dashed rgba(255,255,255,0.18)",
          borderRadius: 12,
        }}
      />
    );
  }

  return cells;
}

function SlideContainer(props: { title: string; hintRight?: string; children: React.ReactNode }) {
  const slideId = React.useId();
  const { activeSlideId, setActiveSlideId } = React.useContext(SlidePresentationContext);
  const pageLayout = React.useContext(LayoutContext);
  const isFullscreen = activeSlideId === slideId;

  const overlayZ = 15000;
  const cardZ = 16000;

  const titleFontSize = React.useMemo(() => {
    if (isFullscreen) return 20;
    if (pageLayout === 6) return 11;
    if (pageLayout === 4) return 13;
    if (pageLayout === 3) return 15;
    if (pageLayout === 2) return 17;
    return 18;
  }, [isFullscreen, pageLayout]);

  const titleLineHeight = titleFontSize <= 11 ? 1.15 : titleFontSize <= 13 ? 1.2 : 1.25;

  return (
    <div style={{ height: "100%", minHeight: 0 }}>
      {isFullscreen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,5,20,0.55)",
            zIndex: overlayZ,
          }}
          onClick={() => setActiveSlideId(null)}
        />
      ) : null}

      <section
        style={{
          background: "#0e2b4b",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.24)",
          borderRadius: 12,
          padding: 12,
          boxShadow: "0 4px 14px rgba(0,0,0,0.18)",

          minHeight: 0,
          height: isFullscreen ? "calc(100vh - 32px)" : "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",

          position: isFullscreen ? "fixed" : "relative",
          left: isFullscreen ? "50%" : undefined,
          top: isFullscreen ? "50%" : undefined,
          transform: isFullscreen ? "translate(-50%, -50%)" : undefined,
          width: isFullscreen ? "min(1400px, calc(100vw - 32px))" : "100%",
          zIndex: isFullscreen ? cardZ : "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              fontSize: titleFontSize,
              lineHeight: titleLineHeight,
              fontWeight: 800,
              letterSpacing: 0.2,
              minWidth: 0,
              flex: 1,
              whiteSpace: "normal",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: isFullscreen ? 3 : pageLayout >= 4 ? 3 : 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {props.title}
          </h2>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setActiveSlideId(isFullscreen ? null : slideId)}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.28)",
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
              }}
              title={isFullscreen ? "Свернуть" : "Развернуть"}
            >
              {isFullscreen ? "Свернуть" : "Развернуть"}
            </button>
          </div>
        </div>

        {props.hintRight ? (
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: "rgba(255,255,255,0.78)",
              textAlign: "right",
            }}
          >
            {props.hintRight}
          </div>
        ) : null}

        <div style={{ minHeight: 0, flex: 1, marginTop: 8 }}>{props.children}</div>
      </section>
    </div>
  );
}

function ErrorBox({ error }: { error: string }) {
  if (!error) return null;
  return (
    <div style={{ color: "#b00020", fontSize: 13, marginTop: 8, whiteSpace: "pre-wrap" }}>
      {error}
    </div>
  );
}


function useDims(roleHeader: Record<string, string>, enabled: boolean, filters: Filters) {
  const [financeMap, setFinanceMap] = useState<Map<number, string>>(new Map());
  const [deptMap, setDeptMap] = useState<Map<number, string>>(new Map());
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!enabled) return;

    (async () => {
      setError("");
      try {
        const [finance, dept] = await Promise.all([
          dashboardsFetch<DimItem[]>("/api/v1/app/dims/finance-source", {}, roleHeader),
          filters.mis_id && filters.year
            ? dashboardsFetch<DimItem[]>(
                `/api/v1/app/dims/department?${buildQueryFromFilters(filters).toString()}`,
                {},
                roleHeader,
              )
            : Promise.resolve([]),
        ]);

        const fm = new Map<number, string>();
        finance.forEach((x) => fm.set(x.id, x.name || x.code || String(x.id)));
        setFinanceMap(fm);

        const dm = new Map<number, string>();
        dept.forEach((x) => dm.set(x.id, x.name || x.code || String(x.id)));
        setDeptMap(dm);
      } catch (e: any) {
        setError(`Ошибка справочников (dims): ${String(e?.message || e)}`);
      }
    })();
  }, [
    enabled,
    roleHeader?.["X-Role"],
    filters.mis_id,
    filters.year,
    (filters.finance_source_ids || []).join(","),
  ]);

  return { financeMap, deptMap, dimsError: error };
}

/**
 * SL2 — выбор finance_source_id + year (по клику по сегменту)
 * Реализация: показываем несколько лет (текущий и 3 предыдущих), grouped bars по источникам.
 */
function Slide2FundingYear({
  filters,
  setFilters,
  financeMap,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
}) {
  const now = new Date();
  const baseYear = filters.year ?? now.getUTCFullYear();
  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = baseYear - 3; y <= baseYear; y++) arr.push(y);
    return arr;
  }, [baseYear]);

  const [data, setData] = useState<AggYearFundingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      if (!filters.mis_id) {
        setError("");
        setData([]);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const qs = new URLSearchParams({
          mis_id: filters.mis_id,
          year_from: String(years[0]),
          year_to: String(years[years.length - 1]),
        });
        const rows = await dashboardsFetch<AggYearFundingRow[]>(`/api/v1/app/agg/year-funding?${qs.toString()}`);
        setData(rows || []);
      } catch (e: any) {
        setError(`SL2: ${String(e?.message || e)}`);
        setData([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters.mis_id, years.join(",")]);

  const financeIds = useMemo(() => {
    const s = new Set<number>();
    data.forEach((r) => s.add(r.finance_source_id));
    return Array.from(s);
  }, [data]);

  const series = useMemo(() => {
    return financeIds.map((fid) => {
      const name = data.find((r) => r.finance_source_id === fid)?.finance_source_name;
      const label = name || financeMap.get(fid) || String(fid);
      return {
        name: label,
        type: "bar",
        itemStyle: { color: financeColorByLabel(label) },
        data: years.map((y) => {
          const row = data.find((r) => r.year === y && r.finance_source_id === fid);
          return row ? row.total_count : 0;
        }),
      };
    });
  }, [financeIds, financeMap, data, years]);

  const option = useMemo(() => {
    return {
      tooltip: { ...DEFAULT_ECHARTS_TOOLTIP, trigger: "axis" },
      textStyle: { color: "#fff" },
      legend: { top: 6, left: "center", itemGap: 10, textStyle: { color: "#fff" } },
      grid: { left: 10, right: 10, top: 42, bottom: 34, containLabel: true },
      xAxis: {
        type: "category",
        data: years.map(String),
        axisLabel: { color: "#fff", hideOverlap: true, margin: 10 },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: "#fff", hideOverlap: true, margin: 10 },
      },
      series,
    };
  }, [series, years]);


  const onChartClick = (params: any) => {
    const clickedYear = Number(params?.name);
    const seriesIndex = Number(params?.seriesIndex);

    const clickedFinanceId =
      Number.isFinite(seriesIndex) ? Number(financeIds[seriesIndex]) : NaN;

    // ✅ 0 (НеОпределен) должен проходить
    if (!Number.isFinite(clickedYear) || !Number.isFinite(clickedFinanceId)) return;

    setFilters((prev) => ({
      ...prev,
      finance_source_ids: [clickedFinanceId],
      year: clickedYear,
      month: undefined,
      day: undefined,
    }));
  };

  return (
    <SlideContainer title="Сл2 Количество услуг по ГОБМП и ОСМС по годам" hintRight="Клик: выбрать источник + год">
      {!filters.mis_id ? <div style={{ color: "#fff" }}>Укажите mis_id, чтобы загрузить данные.</div> : null}
      {loading ? <div>Загрузка…</div> : null}
      <ErrorBox error={error} />

      {/* РЕЗИНОВО: график занимает всю доступную высоту карточки */}
      <div style={{ height: "100%", minHeight: 0 }}>
        <ReactECharts
          key={filters.mis_id || "no-mis"}
          option={option as any}
          opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
          style={{ height: "100%", width: "100%" }}
          onEvents={{ click: onChartClick }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </SlideContainer>
  );
}

/**
 * SL3 — по месяцам
 */
function Slide3Months({
  filters,
  setFilters,
  financeMap,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
}) {
  const [rows, setRows] = useState<AggMonthRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const year = filters.year;
  const fsIds = filters.finance_source_ids;
  const financeLabel = resolveFinanceSourceLabel(filters, financeMap);
  const financeColor = useMemo(() => financeColorByLabel(financeLabel), [financeLabel]);
  const yearLabel = year ?? "—";

  useEffect(() => {
    (async () => {
      setError("");
      if (!filters.mis_id || !year || !fsIds?.length) {
        setRows([]);
        return;
      }
      setLoading(true);
      try {
        const qs = buildQueryFromFilters({ ...filters, year, finance_source_ids: fsIds });
        const data = await dashboardsFetch<AggMonthRow[]>(`/api/v1/app/agg/month?${qs.toString()}`);
        setRows(data || []);
      } catch (e: any) {
        setError(`SL3: ${String(e?.message || e)}`);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters.mis_id, year, (fsIds || []).join(",")]);

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  const seriesData = useMemo(() => {
    return months.map((m) => {
      const subset = rows.filter((x) => x.month === m);
      return subset.reduce((acc, r) => acc + (r.total_count || 0), 0);
    });
  }, [months, rows]);

  const option = useMemo(() => {
    return {
      color: financeColor ? [financeColor] : undefined,
      textStyle: { color: "#fff" },
      tooltip: { ...DEFAULT_ECHARTS_TOOLTIP, trigger: "axis" },
      grid: { left: 10, right: 10, top: 42, bottom: 34, containLabel: true },
      xAxis: {
        type: "category",
        data: months.map((m) => formatMonthName(m)),
        axisLabel: { color: "#fff", hideOverlap: true, margin: 10 },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: "#fff", hideOverlap: true, margin: 10 },
      },
      series: [{ type: "bar", data: seriesData }],
    };
  }, [months, seriesData, financeColor]);


  const onClick = (params: any) => {
    const idx = Number(params?.dataIndex);
    const m = Number.isFinite(idx) ? months[idx] : undefined;
    if (!m) return;
    setFilters((prev) => ({
      ...prev,
      month: m,
      day: undefined,
    }));
  };

  return (
    <SlideContainer
      title={`Сл3 Количество услуг по ${financeLabel} в ${yearLabel} году`}
      hintRight="Клик: выбрать месяц"
    >
      {(!filters.mis_id || !year || !fsIds?.length) ? (
        <div style={{ color: "#fff" }}>Укажите mis_id, год и источник финансирования на Сл2.</div>
      ) : null}
      {loading ? <div>Загрузка…</div> : null}
      <ErrorBox error={error} />
      <div style={{ height: "100%", minHeight: 0 }}>
        <ReactECharts
          option={option as any}
          opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
          style={{ height: "100%", width: "100%" }}
          onEvents={{ click: onClick }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </SlideContainer>
  );
}

/**
 * SL4 — по дням
 */
function Slide4Days({
  filters,
  setFilters,
  financeMap,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
}) {
  const [rows, setRows] = useState<AggDayRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const year = filters.year;
  const month = filters.month;
  const fsIds = filters.finance_source_ids;
  const financeLabel = resolveFinanceSourceLabel(filters, financeMap);
  const financeColor = useMemo(() => financeColorByLabel(financeLabel), [financeLabel]);
  const yearLabel = year ?? "—";
  const monthLabel = month ? formatMonthLabel(month) : "—";

  useEffect(() => {
    (async () => {
      setError("");
      if (!filters.mis_id || !year || !month || !fsIds?.length) {
        setRows([]);
        return;
      }
      setLoading(true);
      try {
        const qs = buildQueryFromFilters({ ...filters, year, month, finance_source_ids: fsIds });
        const data = await dashboardsFetch<AggDayRow[]>(`/api/v1/app/agg/day?${qs.toString()}`);
        setRows(data || []);
      } catch (e: any) {
        setError(`SL4: ${String(e?.message || e)}`);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters.mis_id, year, month, (fsIds || []).join(",")]);

  const maxDays = useMemo(() => getDaysInMonth(year ?? new Date().getUTCFullYear(), month ?? 1), [year, month]);
  const days = useMemo(() => Array.from({ length: maxDays }, (_, i) => i + 1), [maxDays]);

  const seriesData = useMemo(() => {
    return days.map((d) => {
      const subset = rows.filter((x) => {
        const date = new Date(x.date);
        return Number.isFinite(date.getTime()) && date.getUTCDate() === d;
      });
      return subset.reduce((acc, r) => acc + (r.total_count || 0), 0);
    });
  }, [days, rows]);

  const option = useMemo(() => {
    return {
      color: financeColor ? [financeColor] : undefined,
      textStyle: { color: "#fff" },
      tooltip: { ...DEFAULT_ECHARTS_TOOLTIP, trigger: "axis" },
      grid: { left: 10, right: 10, top: 42, bottom: 34, containLabel: true },
      xAxis: {
        type: "category",
        data: days.map((d) => formatDayLabel(d)),
        axisLabel: { color: "#fff", hideOverlap: true, margin: 10 },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: "#fff", hideOverlap: true, margin: 10 },
      },
      series: [{ type: "bar", data: seriesData }],
    };
  }, [days, seriesData, financeColor]);

  const onClick = (params: any) => {
    const d = Number(params?.name);
    if (!d) return;
    setFilters((prev) => ({ ...prev, day: d }));
  };

  return (
    <SlideContainer
      title={`Сл4 Количество услуг по ${financeLabel} в ${monthLabel} ${yearLabel} года`}
      hintRight="Клик: выбрать день"
    >
      {(!filters.mis_id || !year || !month || !fsIds?.length) ? (
        <div style={{ color: "#fff" }}>Укажите mis_id, год+источник на Сл2 и месяц на Сл3.</div>
      ) : null}
      {loading ? <div>Загрузка…</div> : null}
      <ErrorBox error={error} />
      <div style={{ height: "100%", minHeight: 0 }}>
        <ReactECharts
          option={option as any}
          opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
          style={{ height: "100%", width: "100%" }}
          onEvents={{ click: onClick }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </SlideContainer>
  );
}


function fmtDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDate(s: string): Date | null {
  // ожидаем YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function clampToToday(d: Date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x > today ? today : x;
}

// Правило по умолчанию:
// - если выбран month -> последняя неделя этого месяца
// - иначе -> последняя неделя года
// - end не позже today
// - диапазон ровно 7 дней (end - 6 дней)
function defaultWeekRangeFromFilters(filters: Filters): { date_from: string; date_to: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const year = filters.year ?? today.getFullYear();

  let end = new Date(today);

  if (filters.month !== undefined && filters.month !== null) {
    // последний день месяца
    const lastDayOfMonth = new Date(year, Number(filters.month), 0); // month: 1..12
    lastDayOfMonth.setHours(0, 0, 0, 0);
    end = clampToToday(lastDayOfMonth);
  } else {
    // последний день года
    const lastDayOfYear = new Date(year, 11, 31);
    lastDayOfYear.setHours(0, 0, 0, 0);
    end = clampToToday(lastDayOfYear);
  }

  const start = new Date(end);
  start.setDate(end.getDate() - 6);

  return { date_from: fmtDate(start), date_to: fmtDate(end) };
}

function DateRangeModal(props: {
  open: boolean;
  title: string;
  initialFrom: string;
  initialTo: string;
  onCancel: () => void;
  onApply: (dateFrom: string, dateTo: string) => void;
}) {
  const [dateFrom, setDateFrom] = React.useState(props.initialFrom);
  const [dateTo, setDateTo] = React.useState(props.initialTo);
  const [err, setErr] = React.useState("");

  useEffect(() => {
    if (!props.open) return;
    setDateFrom(props.initialFrom);
    setDateTo(props.initialTo);
    setErr("");
  }, [props.open, props.initialFrom, props.initialTo]);

  // ВАЖНО: никаких новых хуков после этого return (иначе React #310)
  if (!props.open) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const onOk = () => {
    const a = parseDate(dateFrom);
    const b = parseDate(dateTo);
    if (!a || !b) {
      setErr("Введите корректные даты (YYYY-MM-DD).");
      return;
    }
    if (b.getTime() < a.getTime()) {
      setErr("Дата 'до' должна быть больше или равна дате 'от'.");
      return;
    }

    // ВАЖНО: лимит 7 дней убран — табличная часть может быть за любой период.
    setErr("");
    props.onApply(dateFrom, dateTo);
  };

  const pageBg = "#083264"; // фон страницы
  const popupBg = "#0e2b4b"; // фон графиков

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: 32,
    padding: "0 10px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.28)",
    outline: "none",
    boxSizing: "border-box",
    minWidth: 0,
    background: pageBg,
    color: "#fff",
    fontSize: 12,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    color: "rgba(255,255,255,0.75)",
    marginBottom: 6,
  };

  const modal = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2147483647,
        padding: 10,
      }}
    >
      {/* стили для календарной иконки / текста в native date input */}
      <style>
        {`
          .drm-date { color-scheme: dark; }
          .drm-date::-webkit-calendar-picker-indicator {
            filter: invert(1);
            opacity: 0.9;
            cursor: pointer;
          }
          .drm-date::-webkit-datetime-edit,
          .drm-date::-webkit-datetime-edit-fields-wrapper,
          .drm-date::-webkit-datetime-edit-text,
          .drm-date::-webkit-datetime-edit-month-field,
          .drm-date::-webkit-datetime-edit-day-field,
          .drm-date::-webkit-datetime-edit-year-field {
            color: #fff;
          }
        `}
      </style>

      <div
        style={{
          width: 420,
          maxWidth: "92vw",
          borderRadius: 14,
          background: popupBg,
          border: "1px solid rgba(255,255,255,0.28)",
          padding: 12,
          boxSizing: "border-box",
          color: "#fff",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>{props.title}</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <div style={labelStyle}>Дата от</div>
            <input
              className="drm-date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || undefined}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={labelStyle}>Дата до</div>
            <input
              className="drm-date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              max={new Date(today.getTime() + 24 * 3600 * 1000).toISOString().slice(0, 10)}
              style={inputStyle}
            />
          </div>
        </div>

        {err ? <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 12 }}>{err}</div> : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button
            type="button"
            onClick={props.onCancel}
            style={{
              height: 32,
              padding: "0 10px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.28)",
              background: "transparent",
              color: "#fff",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={onOk}
            style={{
              height: 32,
              padding: "0 10px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.28)",
              background: "#083264",
              color: "#fff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Применить
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}



/**
 * SL5 — Статистика (не меняет фильтры, только drill-down)
 * В ТЗ тип "как в визуале", поэтому делаем безопасный KPI-блок:
 * берём сумму по месяцу (если есть) иначе по году, иначе 0.
 */
// Сл5 «соисполнители»: по письму Карасайской ЦРБ пончик заменён таблицей
// (SoiTableSlide в AppReportSlides.tsx) — клик по строке выбирает соисполнителя,
// кнопка ⊞ открывает ту же табличную часть, что раньше двойной клик.
const reportFetch = <T,>(path: string) => dashboardsFetch<T>(path);

function Slide5Stats({
  filters,
  setFilters,
  financeMap,
  requestOpenTableWithModal,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  requestOpenTableWithModal: (payload: PendingTablePayload) => void;
}) {
  const financeLabel = useMemo(() => resolveFinanceSourceLabel(filters, financeMap), [filters, financeMap]);
  const yearLabel = filters.year ?? "—";
  return (
    <SoiTableSlide
      filters={filters}
      fetcher={reportFetch}
      Container={SlideContainer}
      title={`Сл5 Количество и сумма услуг по ${financeLabel} в разрезе соисполнителей в ${yearLabel} году`}
      selectedSoiId={filters.soi_id}
      onSelect={(soiId) => setFilters((prev) => ({ ...prev, soi_id: prev.soi_id === soiId ? undefined : soiId }))}
      onOpenTable={(soiId, name) =>
        requestOpenTableWithModal({
          tableKey: "soi-services",
          entity_name: name,
          extra: { soi_id: soiId },
          title: "Период для табличной части (Сл5)",
        })
      }
    />
  );
}


/**
 * Универсальный горизонтальный bar (desc) с кликом и drilldown
 */
function HorizontalBarSlide(props: {
  title: string;
  hintRight?: string;
  endpoint: string; // /api/v1/app/agg/xxx
  filters: Filters;
  barColor?: string;
  mapRows: (rows: any[]) => AggBarRow[];
  onSelect: (row: AggBarRow) => void;
  onOpenTable: (row?: AggBarRow) => void;
  onEvents?: Record<string, any>;
}) {
  const [rows, setRows] = useState<AggBarRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const queryKey = useMemo(
    () => buildQueryFromFilters(props.filters).toString(),
    [props.filters],
  );

  useEffect(() => {
    setError("");

    const hasMis = !!props.filters.mis_id;

    // ⬇️ ВАЖНО: для Сл5–Сл10 достаточно ТОЛЬКО mis_id.
    // year/finance_source_ids/прочее — опционально (если задано, уйдёт в запрос и отработает как раньше).
    if (!hasMis) {
      setRows([]);
      setLoading(false);
      return;
    }


    setLoading(true);

    (async () => {
      try {
        const qs = buildQueryFromFilters(props.filters).toString();
        const data = await dashboardsFetch<any[]>(
          `${props.endpoint}?${qs}`,
        );
        const mapped = props.mapRows(Array.isArray(data) ? data : []);
        const sorted = mapped
          .slice()
          .sort(
            (a, b) => (b.total_count || 0) - (a.total_count || 0),
          );
        setRows(sorted);
      } catch (e: any) {
        setError(`${props.title}: ${String(e?.message || e)}`);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [props.endpoint, queryKey]);

  const option = useMemo(() => {
    const names = rows.map((r) => r.name);
    const rawVals = rows.map((r) => Number(r.total_count || 0));

    const positives = rawVals.filter(
      (v) => Number.isFinite(v) && v > 0,
    );
    const minPositive = positives.length
      ? Math.min(...positives)
      : 1;
    const maxPositive = positives.length
      ? Math.max(...positives)
      : 1;

    const axisMin = Math.max(1e-6, minPositive * 0.9);
    const eps = axisMin * 1.001;
    const logVals = rawVals.map((v) => (v > 0 ? v : eps));

    return {
      backgroundColor: "#0e2b4b",
      color: props.barColor ? [props.barColor] : undefined,
      textStyle: { color: "#fff" },
      tooltip: { ...DEFAULT_ECHARTS_TOOLTIP, trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (items: any) => {
          const it = Array.isArray(items) ? items[0] : items;
          const idx = Number(it?.dataIndex);
          const real = Number.isFinite(idx) ? rawVals[idx] : undefined;
          const name =
            it?.axisValueLabel ?? it?.name ?? "";
          return `${name}<br/>${real ?? ""}`;
        },
      },
      grid: { left: 8, right: 34, top: 10, bottom: 10, containLabel: false },
      xAxis: {
        type: "log",
        logBase: 10,
        min: axisMin,
        max: maxPositive * 1.05,
        minorSplitLine: { show: true },
        axisLabel: {
          color: "#fff",
          formatter: (v: any) => {
            const n = Number(v);
            if (!Number.isFinite(n)) return "";
            if (n === 1) return "1";
            if (n === 10) return "10";
            if (n === 100) return "100";
            if (n === 1000) return "1000";
            return "";
          },
        },
      },
      yAxis: {
        type: "category",
        data: names,
        inverse: true,
        axisLabel: { color: "#fff", width: 190, overflow: "truncate", ellipsis: "…", margin: 10 },
      },
      series: [
        {
          type: "bar",
          data: logVals,
          barMinHeight: 3,
        },
      ],
      dataZoom: [
        { type: "inside", yAxisIndex: 0, filterMode: "none" },
        {
          type: "slider",
          yAxisIndex: 0,
          filterMode: "none",
          right: 8,
          top: 10,
          bottom: 10,
          width: 14,
          showDetail: false,
        },
      ],
    };
  }, [rows, props.barColor]);

  const defaultChartEvents = useMemo(
    () => ({
      click: (params: any) => {
        const idx = Number(params?.dataIndex);
        const row = Number.isFinite(idx) ? rows[idx] : undefined;
        if (!row) return;
        props.onSelect(row);
      },
      dblclick: (params: any) => {
        const idx = Number(params?.dataIndex);
        const row = Number.isFinite(idx) ? rows[idx] : undefined;
        if (!row) return;
        props.onOpenTable(row);
      },
    }),
    [rows, props],
  );

  const mergedEvents = useMemo(
    () => ({ ...defaultChartEvents, ...(props.onEvents || {}) }),
    [defaultChartEvents, props.onEvents],
  );

  const hasHintMissing = !props.filters.mis_id;

  return (
    <SlideContainer
      title={props.title}
      hintRight={
        props.hintRight ||
        "Клик: выбрать значение • Двойной клик: таблица"
      }
    >

      {hasHintMissing ? (
        <div
          style={{ color: "#fff", fontSize: 12, marginBottom: 8 }}
        >
          Укажи mis_id.
        </div>
      ) : null}

      {loading ? <div>Загрузка…</div> : null}
      <ErrorBox error={error} />
      <div style={{ height: "100%", minHeight: 0 }}>
        <ReactECharts
          option={option as any}
          opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
          style={{ height: "100%", width: "100%" }}
          onEvents={mergedEvents}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </SlideContainer>
  );
}




/**
 * SL6 — TreatmentReasonID
 */
function Slide6TreatmentReason({
  filters,
  setFilters,
  financeMap,
  requestOpenTableWithModal,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  requestOpenTableWithModal: (payload: PendingTablePayload) => void;
}) {
  const chartFilters = useMemo(
    () => ({
      ...filters,
      soi_id: undefined,
    }),
    [filters],
  );

  const financeLabel = useMemo(() => resolveFinanceSourceLabel(filters, financeMap), [filters, financeMap]);
  const financeColor = useMemo(() => resolveFinanceColor(filters, financeMap), [filters.finance_source_ids, financeMap]);
  const yearLabel = filters.year ?? "—";

  return (
    <HorizontalBarSlide
      title={`Сл6 Количество услуг по ${financeLabel} с ПО в ${yearLabel} году`}
      endpoint="/api/v1/app/agg/treatment-reason"
      filters={chartFilters}
      barColor={financeColor}
      mapRows={(items) => {
        const takeWords = (s: string, maxWords: number) => {
          const words = (s || "").trim().split(/\s+/).filter(Boolean);
          return words.slice(0, maxWords).join(" ");
        };

        const mapped = (items || []).map((row: any) => {
          const code = Number(row?.treatment_reason_code ?? 0);
          const purpose = Number(row?.treatment_reason_purpose ?? 0);

          const trNameRaw = String(row?.treatment_reason_name ?? "").trim();

          let label = `ПО-${code}`;
          let tail = trNameRaw;

          if (!tail || /^Код\s+\d+/i.test(tail)) {
            tail = purpose ? `цель-${purpose}` : "";
          } else {
            tail = takeWords(tail, 3);
          }

          if (tail) label = `${label} / ${tail}`;

          return {
            id: `${code}:${purpose}`,
            name: label,
            total_count: Number(row?.total_count ?? 0),
          } as AggBarRow;
        });

        return filters.finance_source_ids?.length ? mapped : collapseAggRows(mapped);
      }}
      onSelect={(row) => {
        const [codeRaw, purposeRaw] = row.id.split(":");
        const code = Number(codeRaw);
        const purpose = Number(purposeRaw);
        if (!Number.isFinite(code) || !Number.isFinite(purpose)) return;

        setFilters((prev) => ({
          ...prev,
          treatment_reason: { code, purpose },
          treatment_reason_label: row.name,
        }));
      }}
      onOpenTable={(row) => {
        const entityLabel = row?.name || resolveTreatmentReasonLabel(filters);
        const [codeRaw, purposeRaw] = row?.id?.split(":") || [];
        const code = Number(codeRaw ?? filters.treatment_reason?.code);
        const purpose = Number(purposeRaw ?? filters.treatment_reason?.purpose);
        if (!Number.isFinite(code) || !Number.isFinite(purpose)) return;

        requestOpenTableWithModal({
          tableKey: "treatment-reason-services",
          entity_name: entityLabel,
          extra: { tr_code: code, tr_purpose: purpose },
          title: "Период для табличной части (Сл6)",
        });
      }}
    />
  );
}




/**
 * SL7 — Soi
 */
function Slide7Departments({
  filters,
  setFilters,
  financeMap,
  requestOpenTableWithModal,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  requestOpenTableWithModal: (payload: PendingTablePayload) => void;
}) {
  const chartFilters = useMemo(
    () => ({
      ...filters,
      soi_id: undefined,
    }),
    [filters],
  );

  const financeLabel = useMemo(() => resolveFinanceSourceLabel(filters, financeMap), [filters, financeMap]);
  const financeColor = useMemo(() => resolveFinanceColor(filters, financeMap), [filters.finance_source_ids, financeMap]);
  const yearLabel = filters.year ?? "—";
  const treatmentReasonLabel = resolveTreatmentReasonLabel(filters);

  const hasTreatmentReason =
    !!String(filters.treatment_reason_label ?? "").trim() ||
    (filters.treatment_reason?.code !== undefined && filters.treatment_reason?.purpose !== undefined);

  const treatmentReasonPart = hasTreatmentReason ? ` с ${treatmentReasonLabel}` : "";

  return (
    <HorizontalBarSlide
      title={`Сл7 Количество услуг по ${financeLabel}${treatmentReasonPart} по отделениям за ${yearLabel} год`}
      endpoint="/api/v1/app/agg/departments"
      filters={chartFilters}
      barColor={financeColor}
      mapRows={(rows) => {
        const mapped = (rows || []).map((row: any) => ({
          id: String(row.department_id),
          name: row.department_name || String(row.department_id),
          total_count: Number(row.total_count ?? 0),
        }));

        return filters.finance_source_ids?.length ? mapped : collapseAggRows(mapped);
      }}
      onSelect={(row) => {
        const depId = Number(row.id);
        if (!Number.isFinite(depId)) return;
        setFilters((prev) => ({ ...prev, department_id: depId }));
      }}
      onOpenTable={(row) => {
        const depId = Number(row?.id ?? filters.department_id);
        if (!Number.isFinite(depId)) return;
        requestOpenTableWithModal({
          tableKey: "department-services",
          entity_name: row?.name || String(depId),
          extra: { department_id: depId },
          title: "Период для табличной части (Сл7)",
        });
      }}
    />
  );
}



/**
 * SL8 — Doctors
 */
function Slide8Doctors({
  filters,
  setFilters,
  financeMap,
  requestOpenTableWithModal,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  requestOpenTableWithModal: (payload: PendingTablePayload) => void;
}) {
  const chartFilters = useMemo(
    () => ({
      ...filters,
      soi_id: undefined,
    }),
    [filters],
  );

  const financeLabel = useMemo(() => resolveFinanceSourceLabel(filters, financeMap), [filters, financeMap]);
  const financeColor = useMemo(() => resolveFinanceColor(filters, financeMap), [filters.finance_source_ids, financeMap]);
  const yearLabel = filters.year ?? "—";
  const treatmentReasonLabel = resolveTreatmentReasonLabel(filters);

  const hasTreatmentReason =
    !!String(filters.treatment_reason_label ?? "").trim() ||
    (filters.treatment_reason?.code !== undefined && filters.treatment_reason?.purpose !== undefined);

  const treatmentReasonPart = hasTreatmentReason ? ` с ${treatmentReasonLabel}` : "";

  return (
    <HorizontalBarSlide
      title={`Сл8 Количество услуг по ${financeLabel}${treatmentReasonPart} по врачам за ${yearLabel} год`}
      endpoint="/api/v1/app/agg/doctors"
      filters={chartFilters}
      barColor={financeColor}
      mapRows={(rows) => {
        const mapped = (rows || []).map((row: any) => ({
          id: String(row.performer_employee),
          name: String(row.performer_employee_name ?? row.doctor_name ?? row.performer_employee),
          total_count: Number(row.total_count ?? 0),
        }));

        return filters.finance_source_ids?.length ? mapped : collapseAggRows(mapped);
      }}
      onSelect={(row) => {
        const performerId = String(row?.id ?? "").trim();
        const performerName = String(row?.name ?? "").trim();
        if (!performerId) return;

        setFilters((prev) => ({
          ...prev,
          performer_employee: performerId,
          performer_employee_name: performerName || performerId,
        }));
      }}
      onOpenTable={(row) => {
        const performerId = String(row?.id ?? filters.performer_employee ?? "").trim();
        if (!performerId) return;
        requestOpenTableWithModal({
          tableKey: "doctor-services",
          entity_name: row?.name || String(performerId),
          extra: { performer_employee: performerId },
          title: "Период для табличной части (Сл8)",
        });
      }}
    />
  );
}




/**
 * SL9 — MKB10
 */
function Slide9Mkb10({
  filters,
  setFilters,
  financeMap,
  requestOpenTableWithModal,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  requestOpenTableWithModal: (payload: PendingTablePayload) => void;
}) {
  const chartFilters = useMemo(
    () => ({
      ...filters,
      soi_id: undefined,
      mkb10_id: undefined,
      mkb10: undefined,
    }),
    [filters],
  );

  const financeLabel = useMemo(() => resolveFinanceSourceLabel(filters, financeMap), [filters, financeMap]);
  const financeColor = useMemo(() => resolveFinanceColor(filters, financeMap), [filters.finance_source_ids, financeMap]);
  const yearLabel = filters.year ?? "—";
  const treatmentReasonLabel = resolveTreatmentReasonLabel(filters);

  const hasTreatmentReason =
    !!String(filters.treatment_reason_label ?? "").trim() ||
    (filters.treatment_reason?.code !== undefined && filters.treatment_reason?.purpose !== undefined);

  const treatmentReasonPart = hasTreatmentReason ? ` с ${treatmentReasonLabel}` : "";

  return (
    <HorizontalBarSlide
      title={`Сл9 Количество услуг по ${financeLabel}${treatmentReasonPart} по МКБ10 за ${yearLabel} год`}
      endpoint="/api/v1/app/agg/mkb10"
      filters={chartFilters}
      barColor={financeColor}
      mapRows={(rows) => {
        const mapped = (rows || []).map((row: any) => ({
          id: String(row.mkb10_id),
          name: row.mkb10 ? (row.mkb10_name ? `${row.mkb10} ${row.mkb10_name}` : row.mkb10) : String(row.mkb10_id),
          total_count: Number(row.total_count ?? 0),
        }));

        return filters.finance_source_ids?.length ? mapped : collapseAggRows(mapped);
      }}
      onSelect={(row) => {
        const mkb10Id = Number(row.id);
        // в подписи теперь «код название» — в фильтр уходит только код
        const mkb10Name = String(row?.name ?? "").trim().split(" ")[0];

        setFilters((prev) => ({
          ...prev,
          mkb10_id: Number.isFinite(mkb10Id) ? mkb10Id : undefined,
          mkb10: mkb10Name || undefined,
        }));
      }}
      onOpenTable={(row) => {
        const mkb10Id = Number(row?.id ?? filters.mkb10_id);
        if (!Number.isFinite(mkb10Id)) return;
        requestOpenTableWithModal({
          tableKey: "mkb10-services",
          entity_name: row?.name || String(mkb10Id),
          extra: { mkb10_id: mkb10Id },
          title: "Период для табличной части (Сл9)",
        });
      }}
    />
  );
}



/**
 * SL10 — Количество услуг по отделениям
 */
function Slide10DepartmentsSummary({
  filters,
  setFilters,
  financeMap,
  requestOpenTableWithModal,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  requestOpenTableWithModal: (payload: PendingTablePayload) => void;
}) {
  // ✅ НИЧЕГО не подставляем. Если finance_source_ids не задан — не шлём его.
  const chartFilters = useMemo(
    () => ({
      mis_id: filters.mis_id,
      finance_source_ids: filters.finance_source_ids,
      year: filters.year,
      month: filters.month,
      day: filters.day,
    }),
    [filters.mis_id, filters.finance_source_ids, filters.year, filters.month, filters.day],
  );

  const financeLabel = useMemo(() => resolveFinanceSourceLabel(filters, financeMap), [filters, financeMap]);
  const financeColor = useMemo(() => resolveFinanceColor(filters, financeMap), [filters.finance_source_ids, financeMap]);
  const yearLabel = filters.year ?? "—";

  return (
    <HorizontalBarSlide
      title={`Сл10 Количество услуг по ${financeLabel} в разрезе отделений за ${yearLabel} год`}
      endpoint="/api/v1/app/agg/departments"
      filters={chartFilters as Filters}
      barColor={financeColor}
      mapRows={(rows) => {
        const mapped = (rows || []).map((row: any) => ({
          id: String(row.department_id),
          name: row.department_name || String(row.department_id),
          total_count: Number(row.total_count ?? 0),
        }));

        return filters.finance_source_ids?.length ? mapped : collapseAggRows(mapped);
      }}
      onSelect={(row) => {
        const depId = Number(row.id);
        if (!Number.isFinite(depId)) return;
        setFilters((prev) => ({
          ...prev,
          department_id_10: depId,
          month_d: undefined,
          day_d: undefined,
          performer_employee_13: undefined,
          month_p: undefined,
          day_p: undefined,
        }));
      }}
      onOpenTable={(row) => {
        const depId = Number(row?.id ?? filters.department_id_10);
        if (!Number.isFinite(depId) || !filters.year) return;
        requestOpenTableWithModal({
          tableKey: "department10-services",
          entity_name: row?.name || String(depId),
          extra: { department_id_10: depId, year: filters.year },
          title: "Период для табличной части (Сл10)",
        });
      }}
    />
  );
}




/**
 * SL11 — MKB10 (месяцы)
 */
function Slide11DepartmentMonths({
  filters,
  setFilters,
  financeMap,
  deptMap,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  deptMap: Map<number, string>;
}) {
  const [series, setSeries] = useState<Array<{ name: string; data: number[] }>>([]);
  const [buckets, setBuckets] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ если нет года/ИФ/department_10 — Сл11 НЕ должен открываться вообще
  const canOpen =
    !!filters.mis_id &&
    !!filters.year &&
    Array.isArray(filters.finance_source_ids) &&
    filters.finance_source_ids.length > 0 &&
    filters.department_id_10 !== undefined;

  // Хуки должны вызываться ВСЕГДА (нельзя return null до useMemo/useEffect)
  const departmentLabel = canOpen ? resolveDepartmentLabel(filters.department_id_10, deptMap) : "—";
  const financeLabel = resolveFinanceSourceLabel(filters, financeMap);
  const financeColor = useMemo(() => resolveFinanceColor(filters, financeMap), [filters.finance_source_ids, financeMap]);
  const yearLabel = filters.year ?? "—";

  const baseFilters = useMemo(
    () => ({
      mis_id: filters.mis_id,
      finance_source_ids: filters.finance_source_ids,
      year: filters.year,
    }),
    [filters.mis_id, filters.year, (filters.finance_source_ids || []).join(",")],
  );

  const queryKey = useMemo(() => {
    if (!canOpen) return "closed";
    return `${buildAggQuery(baseFilters as Filters).toString()}|${filters.department_id_10 ?? "none"}`;
  }, [canOpen, baseFilters, filters.department_id_10]);

  useEffect(() => {
    if (!canOpen) {
      // слайд закрыт — гарантированно чистим состояние и не грузим данные
      setError("");
      setLoading(false);
      setSeries([]);
      setBuckets([]);
      return;
    }

    (async () => {
      setError("");
      setLoading(true);

      try {
        const qs = buildAggQuery(baseFilters as Filters, {
          department_id: filters.department_id_10,
        }).toString();

        const res = await dashboardsFetch<AggSeriesResponse>(`/api/v1/app/agg/departments/series?${qs}`);

        const nextBuckets = res?.buckets || [];
        const nextSeries = (res?.series || []).map((item) => ({
          name: item.name,
          data: item.values || [],
        }));

        setBuckets(nextBuckets);
        setSeries(nextSeries);
      } catch (e: any) {
        setError(`Сл11: ${String(e?.message || e)}`);
        setSeries([]);
        setBuckets([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [queryKey, canOpen, baseFilters, filters.department_id_10]);

  const option = useMemo(() => {
    return {
      color: financeColor ? [financeColor] : undefined,
      textStyle: { color: "#fff" },
      tooltip: { ...DEFAULT_ECHARTS_TOOLTIP, trigger: "axis" },
      legend: { type: "scroll", top: 6, left: "center", itemGap: 10, textStyle: { color: "#fff" } },
      grid: { left: 10, right: 10, top: 48, bottom: 28, containLabel: true },
      xAxis: {
        type: "category",
        data: buckets.map((m) => formatMonthName(m)),
        axisLabel: { color: "#fff", hideOverlap: true, margin: 10 },
      },
      yAxis: { type: "value", axisLabel: { color: "#fff", hideOverlap: true, margin: 10 } },
      series: series.map((s) => ({ type: "bar", name: s.name, data: s.data })),
    };
  }, [buckets, series, financeColor]);

  const onClick = (params: any) => {
    const idx = Number(params?.dataIndex);
    const m = Number.isFinite(idx) ? buckets[idx] : undefined;
    if (!m) return;

    setFilters((prev) => ({
      ...prev,
      month_d: m,
      day_d: undefined,
      performer_employee_13: undefined,
      month_p: undefined,
      day_p: undefined,
    }));
  };

  // ✅ return null теперь ПОСЛЕ хуков — React #310 исчезает
  if (!canOpen) {
    return null;
  }

  return (
    <SlideContainer
      title={`Сл11 Количество услуг по ${financeLabel} за ${yearLabel} год по ${departmentLabel}`}
      hintRight="Клик: выбрать месяц"
    >
      {loading ? <div>Загрузка…</div> : null}
      <ErrorBox error={error} />

      <div style={{ height: "100%", minHeight: 0 }}>
        <ReactECharts
          option={option as any}
          opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
          style={{ height: "100%", width: "100%" }}
          onEvents={{ click: onClick }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </SlideContainer>
  );
}



/**
 * SL12 — Department (ВА/ФАП и другие подразделения)
 */
function Slide12DepartmentDays({
  filters,
  setFilters,
  financeMap,
  deptMap,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  deptMap: Map<number, string>;
}) {
  const [series, setSeries] = useState<Array<{ name: string; data: number[] }>>([]);
  const [buckets, setBuckets] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const departmentLabel = resolveDepartmentLabel(filters.department_id_10, deptMap);
  const financeLabel = resolveFinanceSourceLabel(filters, financeMap);
  const financeColor = useMemo(
  () => resolveFinanceColor(filters, financeMap),
  [filters.finance_source_ids, financeMap]
  );
  const monthLabel = filters.month_d ? formatMonthLocative(filters.month_d) : "—";

  // ✅ year теперь НЕ обязателен
  const baseFilters = useMemo(
    () => ({
      mis_id: filters.mis_id,
      finance_source_ids: filters.finance_source_ids,
      year: filters.year,
      month: filters.month_d,
    }),
    [filters],
  );

  const queryKey = useMemo(
    () => `${buildAggQuery(baseFilters as Filters).toString()}|${filters.department_id_10 ?? "none"}`,
    [baseFilters, filters.department_id_10],
  );

  useEffect(() => {
    (async () => {
      setError("");

      // ✅ Было: требовали year+month
      // ✅ Стало: достаточно mis_id + month_d + department_id_10
      if (!baseFilters.mis_id || !baseFilters.month || filters.department_id_10 === undefined) {
        setSeries([]);
        setBuckets([]);
        return;
      }

      setLoading(true);
      try {
        const qs = buildAggQuery(baseFilters as Filters, {
          department_id: filters.department_id_10,
        }).toString();

        const res = await dashboardsFetch<AggSeriesResponse>(`/api/v1/app/agg/departments/series?${qs}`);
        const nextBuckets = res?.buckets || [];
        const nextSeries = (res?.series || []).map((item) => ({
          name: item.name,
          data: item.values || [],
        }));

        setBuckets(nextBuckets);
        setSeries(nextSeries);
      } catch (e: any) {
        setError(`Сл12: ${String(e?.message || e)}`);
        setSeries([]);
        setBuckets([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [queryKey]);

  const option = useMemo(() => {
    return {
      color: financeColor ? [financeColor] : undefined,
      textStyle: { color: "#fff" },
      tooltip: { ...DEFAULT_ECHARTS_TOOLTIP, trigger: "axis" },
      legend: { type: "scroll", top: 6, left: "center", itemGap: 10, textStyle: { color: "#fff" } },
      grid: { left: 10, right: 10, top: 48, bottom: 28, containLabel: true },
      xAxis: { type: "category", data: buckets.map((d) => formatDayLabel(d)), axisLabel: { color: "#fff", hideOverlap: true, margin: 10 } },
      yAxis: { type: "value", axisLabel: { color: "#fff", hideOverlap: true, margin: 10 } },
      series: series.map((s) => ({ type: "bar", name: s.name, data: s.data })),
    };
  }, [buckets, series, financeColor]);

  const onClick = (params: any) => {
    const idx = Number(params?.dataIndex);
    const d = Number.isFinite(idx) ? buckets[idx] : undefined;
    if (!d) return;

    setFilters((prev) => ({
      ...prev,
      day_d: d,
      performer_employee_13: undefined,
      month_p: undefined,
      day_p: undefined,
    }));
  };

  // таблица по дням требует точный YYYY-MM-DD => без year не открываем
  const onDblClick = (params: any) => {
    const idx = Number(params?.dataIndex);
    const d = Number.isFinite(idx) ? buckets[idx] : undefined;

    const year = filters.year;
    const month = filters.month_d;
    const dep = filters.department_id_10;

    if (!filters.mis_id || !filters.finance_source_ids?.length || !year || !month || dep === undefined || !d) return;

    const dateStr = new Date(Date.UTC(year, month - 1, d)).toISOString().slice(0, 10);

    openTableByKeyInNewTab(
      "department10-services-day",
      {
        mis_id: filters.mis_id,
        finance_source_ids: filters.finance_source_ids,
      },
      {
        department_id_10: dep,
        date_from: dateStr,
        date_to: dateStr,
      }
    );
  };

  return (
    <SlideContainer
      title={`Сл12 Количество услуг по ${financeLabel} в ${monthLabel} по ${departmentLabel}`}
      hintRight="Клик: выбрать день"
    >
      {!baseFilters.mis_id ? <div style={{ color: "#fff" }}>Укажите mis_id.</div> : null}

      {!baseFilters.month ? (
        <div style={{ color: "#fff" }}>Выберите месяц (Сл11) для детализации по дням.</div>
      ) : null}

      {filters.department_id_10 === undefined ? (
        <div style={{ color: "#fff" }}>Выберите Department_10 на Сл10.</div>
      ) : null}

      {loading ? <div>Загрузка…</div> : null}
      <ErrorBox error={error} />

      <div style={{ height: "100%", minHeight: 0 }}>
        <ReactECharts
          option={option as any}
          opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
          style={{ height: "100%", width: "100%" }}
          onEvents={{ click: onClick, dblclick: onDblClick }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </SlideContainer>
  );
}



/**
 * SL13 — Doctors inside Department (тот же agg/doctor, но контекст уже с Department)
 */
function Slide13DoctorsInDepartment({
  filters,
  setFilters,
  financeMap,
  deptMap,
  requestOpenTableWithModal,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
  deptMap: Map<number, string>;
  requestOpenTableWithModal: (payload: PendingTablePayload) => void;
}) {
  const chartFilters = useMemo(
    () => ({
      mis_id: filters.mis_id,
      finance_source_ids: filters.finance_source_ids,
      year: filters.year,
      month: filters.month_d,
      department_id: filters.department_id_10,
      day: filters.day_d,
    }),
    [filters],
  );
  const departmentLabel = resolveDepartmentLabel(filters.department_id_10, deptMap);

  // ✅ ВАЖНО: берем реальный label по выбранным finance_source_ids,
  // чтобы financeColorByLabel() мог определить ОСМС/ГОБМП как в других слайдах.
  const financeLabel = useMemo(() => resolveFinanceSourceLabel(filters, financeMap), [filters, financeMap]);
  const financeColor = useMemo(() => financeColorByLabel(financeLabel), [financeLabel]);

  if (filters.department_id_10 === undefined) {
    return (
      <SlideContainer title={`Сл13 Количество услуг по медработникам по ${departmentLabel}`}>
        <div style={{ color: "#fff" }}>Выберите Department_10 на Сл10.</div>
      </SlideContainer>
    );
  }

  return (
    <HorizontalBarSlide
      title={`Сл13 Количество услуг по медработникам по ${departmentLabel}`}
      endpoint="/api/v1/app/agg/doctors"
      filters={chartFilters}
      barColor={financeColor}
      mapRows={(items) => {
        return (items || []).map((row: any) => {
          const id = String(row?.performer_employee ?? "");
          const fio = String(row?.performer_employee_name ?? "").trim();
          return {
            id,
            name: fio || id,
            total_count: Number(row?.total_count ?? 0),
          };
        });
      }}
      onSelect={(row) => {
        const performerId = String(row?.id ?? "").trim();
        if (!performerId) return;
        setFilters((prev) => ({
          ...prev,
          performer_employee_13: performerId,
          performer_employee_13_name: String(row?.name ?? "").trim(),
          month_p: undefined,
          day_p: undefined,
        }));
      }}
      onOpenTable={(row) => {
        const performerId = String(row?.id ?? filters.performer_employee_13 ?? "").trim();
        if (!performerId || !filters.year || filters.department_id_10 === undefined) return;
        requestOpenTableWithModal({
          tableKey: "department10-doctor-services",
          entity_name: row?.name || String(performerId),
          extra: {
            department_id_10: filters.department_id_10,
            performer_employee_13: performerId,
            year: filters.year,
            month_d: filters.month_d,
            day_d: filters.day_d,
          },
          title: "Период для табличной части (Сл13)",
        });
      }}
    />
  );
}


/**
 * SL14 — Количество услуг по врачам месяцы
 */
function Slide14DoctorsMonths({
  filters,
  setFilters,
  financeMap,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
}) {
  const [series, setSeries] = useState<Array<{ name: string; data: number[] }>>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [buckets, setBuckets] = useState<number[]>([]);

  const financeLabel = resolveFinanceSourceLabel(filters, financeMap);
  const financeColor = useMemo(
  () => resolveFinanceColor(filters, financeMap),
  [filters.finance_source_ids, financeMap]
  );
  const yearLabel = filters.year ?? "—";
  const performerLabel = resolvePerformerLabel(filters);

  // ✅ year теперь НЕ обязателен
  const baseFilters = useMemo(
    () => ({
      mis_id: filters.mis_id,
      finance_source_ids: filters.finance_source_ids,
      year: filters.year,
      department_id: filters.department_id_10,
    }),
    [filters],
  );

  const queryKey = useMemo(
    () => `${buildAggQuery(baseFilters as Filters).toString()}|${filters.performer_employee_13 ?? "none"}`,
    [baseFilters, filters.performer_employee_13],
  );

  useEffect(() => {
    (async () => {
      setError("");
      setLoading(true);

      // ✅ Было: требовали year
      // ✅ Стало: достаточно mis_id + выбранный performer_employee_13
      if (!baseFilters.mis_id || filters.performer_employee_13 === undefined) {
        setSeries([]);
        setBuckets([]);
        setLoading(false);
        return;
      }

      try {
        const qs = buildAggQuery(baseFilters as Filters, {
          performer_employee: filters.performer_employee_13,
        }).toString();

        const res = await dashboardsFetch<AggSeriesResponse>(`/api/v1/app/agg/doctors/series?${qs}`);
        const nextBuckets = res?.buckets || [];
        const nextSeries = (res?.series || []).map((item) => ({
          name: item.name,
          data: item.values || [],
        }));

        setBuckets(nextBuckets);
        setSeries(nextSeries);
      } catch (e: any) {
        setError(`Сл14: ${String(e?.message || e)}`);
        setSeries([]);
        setBuckets([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [queryKey]);

  const option = useMemo(() => {
    return {
      color: financeColor ? [financeColor] : undefined,
      textStyle: { color: "#fff" },
      tooltip: { ...DEFAULT_ECHARTS_TOOLTIP, trigger: "axis" },
      legend: { type: "scroll", top: 6, left: "center", itemGap: 10, textStyle: { color: "#fff" } },
      grid: { left: 10, right: 10, top: 48, bottom: 28, containLabel: true },
      xAxis: { type: "category", data: buckets.map((m) => formatMonthName(m)), axisLabel: { color: "#fff", hideOverlap: true, margin: 10 } },
      yAxis: { type: "value", axisLabel: { color: "#fff", hideOverlap: true, margin: 10 } },
      series: series.map((s) => ({ type: "bar", name: s.name, data: s.data })),
    };
  }, [buckets, series, financeColor]);

  const onClick = (params: any) => {
    const idx = Number(params?.dataIndex);
    const m = Number.isFinite(idx) ? buckets[idx] : undefined;
    if (!m) return;

    setFilters((prev) => ({
      ...prev,
      month_p: m,
      day_p: undefined,
    }));
  };

  return (
    <SlideContainer
      title={`Сл14 Количество услуг по ${financeLabel} в ${yearLabel} году по ${performerLabel} помесячно`}
      hintRight="Клик: выбрать месяц"
    >
      {filters.performer_employee_13 === undefined ? (
        <div style={{ color: "#fff" }}>Выберите PerformerEmployee_13 на Сл13.</div>
      ) : null}

      {loading ? <div>Загрузка…</div> : null}
      <ErrorBox error={error} />

      <div style={{ height: "100%", minHeight: 0 }}>
        <ReactECharts
          option={option as any}
          opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
          style={{ height: "100%", width: "100%" }}
          onEvents={{ click: onClick }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </SlideContainer>
  );
}


/**
 * SL15 — Количество услуг по врачам дни
 */
function Slide15DoctorsDays({
  filters,
  setFilters,
  financeMap,
}: {
  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;
  financeMap: Map<number, string>;
}) {
  const [series, setSeries] = useState<Array<{ name: string; data: number[] }>>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [buckets, setBuckets] = useState<number[]>([]);

  const financeLabel = resolveFinanceSourceLabel(filters, financeMap);
  const financeColor = useMemo(() => financeColorByLabel(financeLabel), [financeLabel]);
  const yearLabel = filters.year ?? "—";
  const performerLabel = resolvePerformerLabel(filters);

  // ✅ year теперь НЕ обязателен
  const baseFilters = useMemo(
    () => ({
      mis_id: filters.mis_id,
      finance_source_ids: filters.finance_source_ids,
      year: filters.year,
      month: filters.month_p,
      department_id: filters.department_id_10,
    }),
    [filters],
  );

  const queryKey = useMemo(
    () => `${buildAggQuery(baseFilters as Filters).toString()}|${filters.performer_employee_13 ?? "none"}`,
    [baseFilters, filters.performer_employee_13],
  );

  useEffect(() => {
    (async () => {
      setError("");

      // ✅ Было: требовали year+month
      // ✅ Стало: достаточно mis_id + month_p + performer_employee_13
      if (!baseFilters.mis_id || !baseFilters.month || filters.performer_employee_13 === undefined) {
        setSeries([]);
        setBuckets([]);
        return;
      }

      setLoading(true);
      try {
        const qs = buildAggQuery(baseFilters as Filters, {
          performer_employee: filters.performer_employee_13,
        }).toString();

        const res = await dashboardsFetch<AggSeriesResponse>(`/api/v1/app/agg/doctors/series?${qs}`);
        const nextBuckets = res?.buckets || [];
        const nextSeries = (res?.series || []).map((item) => ({
          name: item.name,
          data: item.values || [],
        }));

        setBuckets(nextBuckets);
        setSeries(nextSeries);
      } catch (e: any) {
        setError(`Сл15: ${String(e?.message || e)}`);
        setSeries([]);
        setBuckets([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [queryKey]);

  const option = useMemo(() => {
    return {
      color: financeColor ? [financeColor] : undefined,
      textStyle: { color: "#fff" },
      tooltip: { ...DEFAULT_ECHARTS_TOOLTIP, trigger: "axis" },
      legend: { type: "scroll", top: 6, left: "center", itemGap: 10, textStyle: { color: "#fff" } },
      grid: { left: 10, right: 10, top: 48, bottom: 28, containLabel: true },
      xAxis: { type: "category", data: buckets.map((d) => formatDayLabel(d)), axisLabel: { color: "#fff", hideOverlap: true, margin: 10 } },
      yAxis: { type: "value", axisLabel: { color: "#fff", hideOverlap: true, margin: 10 } },
      series: series.map((s) => ({ type: "bar", name: s.name, data: s.data })),
    };
  }, [buckets, series, financeColor]);

  const onClick = (params: any) => {
    const idx = Number(params?.dataIndex);
    const d = Number.isFinite(idx) ? buckets[idx] : undefined;
    if (!d) return;
    setFilters((prev) => ({ ...prev, day_p: d }));
  };

  // таблица по дням требует точный YYYY-MM-DD => без year не открываем
  const onDblClick = (params: any) => {
    const idx = Number(params?.dataIndex);
    const d = Number.isFinite(idx) ? buckets[idx] : undefined;

    const year = filters.year;
    const month = filters.month_p;
    const dep = filters.department_id_10;
    const performerId = filters.performer_employee_13;

    if (
      !filters.mis_id ||
      !filters.finance_source_ids?.length ||
      !year ||
      !month ||
      dep === undefined ||
      !d ||
      !performerId
    ) return;

    const dateStr = new Date(Date.UTC(year, month - 1, d)).toISOString().slice(0, 10);

    openTableByKeyInNewTab(
      "department10-doctor-services-day",
      {
        mis_id: filters.mis_id,
        finance_source_ids: filters.finance_source_ids,
      },
      {
        department_id_10: dep,
        performer_employee_13: performerId,
        date_from: dateStr,
        date_to: dateStr,
      }
    );
  };

  return (
    <SlideContainer
      title={`Сл15 Количество услуг по ${financeLabel} в ${yearLabel} году по ${performerLabel} по дням`}
      hintRight="Клик: выбрать день"
    >
      {filters.performer_employee_13 === undefined ? (
        <div style={{ color: "#fff" }}>Выберите PerformerEmployee_13 на Сл13.</div>
      ) : null}

      {loading ? <div>Загрузка…</div> : null}
      <ErrorBox error={error} />

      <div style={{ height: "100%", minHeight: 0 }}>
        <ReactECharts
          option={option as any}
          opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
          style={{ height: "100%", width: "100%" }}
          onEvents={{ click: onClick, dblclick: onDblClick }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </SlideContainer>
  );
}




/**
 * Табличная страница: /dashboards/app/table


function downloadCurrentTableAsCsv(tableKey: string, cols: string[], rows: any[]) {
  const escape = (v: any) => {
    const s = String(v ?? "");
    const escaped = s.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const header = cols.map(escape).join(",");
  const body = (rows || [])
    .map((r) => cols.map((c) => escape(r?.[c])).join(","))
    .join("\n");

  const csv = [header, body].filter(Boolean).join("\n");
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${tableKey}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
 */

function TablePage() {
  const qs = new URLSearchParams(window.location.search || "");
  const tableKey = qs.get("table") || "default";

  const entityName = qs.get("entity_name") || qs.get("soi_name") || "";

  const dateFromQ = qs.get("date_from") || undefined;
  const dateToQ = qs.get("date_to") || undefined;

  const tableParams = useMemo(() => {
    const params = new URLSearchParams();
    qs.forEach((value, key) => {
      if (key === "table" || key === "entity_name" || key === "soi_name") return;
      params.append(key, value);
    });
    return params;
  }, [qs.toString()]);

  const tableParamsString = tableParams.toString();
  const hasMisId = Boolean(qs.get("mis_id"));
  const hasFinance = qs.getAll("finance_source_id").length > 0;
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");

        if (!hasMisId || !hasFinance || !dateFromQ || !dateToQ) {
          setRows([]);
          return;
        }

        const data = await dashboardsFetch<any[]>(
          `/api/v1/app/table/${tableKey}?${tableParamsString}`
        );

        setRows(
          (data || []).map((r) => ({
            "№ пп": r.row_num,
            "Код Услуги": r.service_code,
            "Название услуги": r.service_name,
            "Количество": r.qty,
            "Сумма": r.amount,
          }))
        );
      } catch (e: any) {
        setError(String(e?.message || e));
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [tableKey, tableParamsString, hasMisId, hasFinance, dateFromQ, dateToQ]);

  const title = `Таблица ${tableKey} — услуги ${entityName}`;

  const cols = ["№ пп", "Код Услуги", "Название услуги", "Количество", "Сумма"];

  const escapeCsv = (v: any) => {
    const s = v == null ? "" : String(v);
    const esc = s.replace(/"/g, '""');
    return /[;"\n]/.test(esc) ? `"${esc}"` : esc;
  };

  const downloadCsv = () => {
    const headerLine = `Услуги: ${entityName}`;
    const csvLines = [
      headerLine,
      "",
      cols.join(";"),
      ...rows.map((r) => cols.map((c) => escapeCsv(r[c])).join(";")),
    ];

    const csv = "\ufeff" + csvLines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `table_${tableKey}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pageBg = "#3c1f61";
  const cardBg = "#260c45";
  const controlBg = "#4a2a74";
  const headerBg = "#5b3888";
  const borderColor = "rgba(255,255,255,0.22)";
  const textColor = "#fff";
  const mutedText = "rgba(255,255,255,0.82)";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: pageBg,
        color: textColor,
        padding: 16,
      }}
    >
      <div
        style={{
          maxWidth: 1600,
          margin: "0 auto",
          background: cardBg,
          border: `1px solid ${borderColor}`,
          borderRadius: 14,
          boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
          padding: 16,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            marginBottom: 12,
            color: textColor,
          }}
        >
          {title}
        </div>

        {rows.length > 0 && (
          <button
            onClick={downloadCsv}
            style={{
              marginBottom: 12,
              background: controlBg,
              color: textColor,
              border: `1px solid ${borderColor}`,
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Скачать CSV
          </button>
        )}

        {loading && <div style={{ color: mutedText }}>Загрузка…</div>}
        <ErrorBox error={error} />
        {!loading && !error && rows.length === 0 && (
          <div style={{ color: mutedText }}>Нет данных</div>
        )}

        {rows.length > 0 && (
          <div
            style={{
              width: "100%",
              maxHeight: "70vh",
              overflowX: "auto",
              overflowY: "auto",
              border: `1px solid ${borderColor}`,
              borderRadius: 12,
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: cardBg,
                color: textColor,
              }}
            >
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th
                      key={c}
                      style={{
                        border: `1px solid ${borderColor}`,
                        padding: "10px 12px",
                        background: headerBg,
                        color: textColor,
                        textAlign: c === "Название услуги" ? "left" : "center",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                      }}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    style={{
                      background: i % 2 === 0 ? cardBg : "#2d1350",
                    }}
                  >
                    {cols.map((c) => (
                      <td
                        key={c}
                        style={{
                          border: `1px solid ${borderColor}`,
                          padding: "10px 12px",
                          textAlign: c === "Название услуги" ? "left" : "center",
                          verticalAlign: "top",
                          color: textColor,
                        }}
                      >
                        {r[c]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DashboardsAppInner(): JSX.Element {
  const pathname = window.location.pathname || "/";
  const urlMisId = new URLSearchParams(window.location.search || "").get("mis_id")?.trim() || "";
  

  // --- ВСЕ хуки объявляем всегда (без ранних return) ---
  const [userRole, setUserRole] = useState<string>("");
  const [roleLoading, setRoleLoading] = useState<boolean>(true);
  const [roleError, setRoleError] = useState<string>("");
  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([]);
  const [orgsError, setOrgsError] = useState<string>("");

  useEffect(() => {
    (async () => {
      setRoleLoading(true);
      setRoleError("");
      try {
        const role = await fetchUserRoleFromDefektolog();
        setUserRole(role);
        localStorage.setItem("dashboards_role", role);
      } catch (e: any) {
        localStorage.removeItem("dashboards_role");
        setUserRole("");
        setRoleError(String(e?.message || e));
      } finally {
        setRoleLoading(false);
      }
    })();
  }, []);

  const roleAllowed = isDashboardsRoleAllowed(userRole);

  const globalStyle = (
    <style>{`
      * { box-sizing: border-box; }

      html, body, #root {
        height: 100%;
        overflow: hidden;
      }

      body {
        margin: 0;
        font-family: Inter, "Noto Sans", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: geometricPrecision;
      }

      * {
        font-family: inherit;
      }
    `}</style>
  );

  useEffect(() => {
    if (roleLoading || !roleAllowed) return;
    (async () => {
      setOrgsError("");
      try {
        const orgs = await fetchUserOrgsFromDefektolog();
        setOrgOptions(orgs);
      } catch (e: any) {
        setOrgsError(String(e?.message || e));
        setOrgOptions([]);
      }
    })();
  }, [roleAllowed, roleLoading]);

  const roleHeader = useMemo(() => {
    return userRole ? ({ "X-Role": userRole } as Record<string, string>) : ({} as Record<string, string>);
  }, [userRole]);

  const [filters, setFiltersState] = useState<Filters>(() => (urlMisId ? { mis_id: urlMisId } : {}));
  const { financeMap, deptMap, dimsError } = useDims(roleHeader, !roleLoading && roleAllowed, filters);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [pendingTable, setPendingTable] = useState<PendingTablePayload | null>(null);

  useEffect(() => {
    if (filters.mis_id || !orgOptions.length) return;
    if (orgOptions.length === 1) {
      setFiltersState((prev) => ({ ...prev, mis_id: orgOptions[0].id }));
    }
  }, [filters.mis_id, orgOptions]);

  useEffect(() => {
    if (!urlMisId || filters.mis_id) return;
    setFiltersState((prev) => ({ ...prev, mis_id: urlMisId }));
  }, [urlMisId, filters.mis_id]);

  const setFilters = (updater: (prev: Filters) => Filters) => {
    setFiltersState((prev) => updater(prev));
  };

  const defaultRange = useMemo(() => defaultWeekRangeFromFilters(filters), [filters.year, filters.month]);

  const closeTableModal = () => {
    setRangeOpen(false);
    setPendingTable(null);
  };

  const requestOpenTableWithModal = (payload: PendingTablePayload) => {
    setPendingTable(payload);
    setRangeOpen(true);
  };

  const clearFilter = (key: keyof Filters) => {
    setFilters((prev) => {
      const next: Filters = { ...prev };

      if (key === "treatment_reason") {
        next.treatment_reason = null;
        delete next.treatment_reason_label;
      } else if (key === "mkb10_id") {
        delete next.mkb10_id;
        delete next.mkb10;
      } else if (key === "department_id_10") {
        delete next.department_id_10;
        delete next.month_d;
        delete next.day_d;
        delete next.performer_employee_13;
        delete next.performer_employee_13_name;
        delete next.month_p;
        delete next.day_p;
      } else if (key === "performer_employee") {
        delete next.performer_employee;
        delete next.performer_employee_name;
      } else if (key === "performer_employee_13") {
        delete next.performer_employee_13;
        delete next.performer_employee_13_name;
        delete next.month_p;
        delete next.day_p;
      } else if (key === "month_d") {
        delete next.month_d;
        delete next.day_d;
      } else if (key === "month_p") {
        delete next.month_p;
        delete next.day_p;
      } else {
        delete (next as any)[key];
      }

      if (key === "year") {
        delete next.month;
        delete next.day;
      }
      if (key === "month") {
        delete next.day;
      }

      return next;
    });
  };

  const resetAll = () => {
    setFiltersState({ mis_id: filters.mis_id });
  };

  const chips = useMemo(() => {
    const out: Array<{ key: keyof Filters; label: string }> = [];

    if (filters.mis_id) {
      const orgLabel = orgOptions.find((x) => x.id === filters.mis_id)?.label || filters.mis_id;
      out.push({ key: "mis_id", label: `Подразделение: ${orgLabel}` });
    }

    if (filters.finance_source_ids?.length) {
      const labels = filters.finance_source_ids
        .map((id) => financeMap.get(id) || String(id))
        .join(", ");
      out.push({ key: "finance_source_ids", label: `Источник финансирования: ${labels}` });
    }

    if (filters.year) {
      out.push({ key: "year", label: `Год: ${filters.year}` });
    }

    if (filters.month) {
      out.push({ key: "month", label: `Месяц: ${formatMonthName(filters.month)}` });
    }

    if (filters.day) {
      const monthPart = filters.month ? ` ${formatMonthGenitive(filters.month)}` : "";
      out.push({ key: "day", label: `День: ${formatDayLabel(filters.day)}${monthPart}` });
    }

    if (filters.treatment_reason?.code !== undefined && filters.treatment_reason?.purpose !== undefined) {
      out.push({
        key: "treatment_reason",
        label: `Повод обращения: ${resolveTreatmentReasonLabel(filters)}`,
      });
    }

    if (filters.department_id !== undefined) {
      out.push({
        key: "department_id",
        label: `Отделение: ${deptMap.get(filters.department_id) || filters.department_id}`,
      });
    }

    if (filters.performer_employee !== undefined) {
      out.push({
        key: "performer_employee",
        label: `Врач: ${filters.performer_employee_name || filters.performer_employee}`,
      });
    }

    if (filters.mkb10_id !== undefined) {
      out.push({
        key: "mkb10_id",
        label: `МКБ-10: ${filters.mkb10 || filters.mkb10_id}`,
      });
    }

    if (filters.department_id_10 !== undefined) {
      out.push({
        key: "department_id_10",
        label: `Отделение_10: ${deptMap.get(filters.department_id_10) || filters.department_id_10}`,
      });
    }

    if (filters.month_d !== undefined) {
      out.push({
        key: "month_d",
        label: `Месяц детализации: ${formatMonthName(filters.month_d)}`,
      });
    }

    if (filters.day_d !== undefined) {
      const monthPart = filters.month_d ? ` ${formatMonthGenitive(filters.month_d)}` : "";
      out.push({
        key: "day_d",
        label: `День детализации: ${formatDayLabel(filters.day_d)}${monthPart}`,
      });
    }

    if (filters.performer_employee_13 !== undefined) {
      out.push({
        key: "performer_employee_13",
        label: `Медработник: ${filters.performer_employee_13_name || filters.performer_employee_13}`,
      });
    }

    if (filters.month_p !== undefined) {
      out.push({
        key: "month_p",
        label: `Месяц медработника: ${formatMonthName(filters.month_p)}`,
      });
    }

    if (filters.day_p !== undefined) {
      const monthPart = filters.month_p ? ` ${formatMonthGenitive(filters.month_p)}` : "";
      out.push({
        key: "day_p",
        label: `День медработника: ${formatDayLabel(filters.day_p)}${monthPart}`,
      });
    }

    return out;
  }, [filters, financeMap, deptMap, orgOptions]);

  const [layoutByPage, setLayoutByPage] = useState<Record<number, PageLayout>>(() => readLayoutByPageFromStorage());
  const [pageIndex, setPageIndex] = useState(0);
  const [activeSlideId, setActiveSlideId] = useState<string | null>(null);

  const hasSlide2Selection =
    !!filters.mis_id &&
    filters.year !== undefined &&
    Array.isArray(filters.finance_source_ids) &&
    filters.finance_source_ids.length > 0;
  const hasSlide3Selection = hasSlide2Selection && filters.month !== undefined && filters.month !== null;
  const hasSlide10Selection = filters.department_id_10 !== undefined;
  const hasSlide11Selection = hasSlide10Selection && filters.month_d !== undefined && filters.month_d !== null;
  const hasSlide12Selection = hasSlide11Selection && filters.day_d !== undefined && filters.day_d !== null;
  const hasSlide13Selection = filters.performer_employee_13 !== undefined;
  const hasSlide14Selection = hasSlide13Selection && filters.month_p !== undefined && filters.month_p !== null;

  const visibleSlides = useMemo<SlideItem[]>(() => {
    const slides: SlideItem[] = [
      { id: "slide-2", element: <Slide2FundingYear filters={filters} setFilters={setFilters} financeMap={financeMap} /> },
      ...(hasSlide2Selection ? [{ id: "slide-3", element: <Slide3Months filters={filters} setFilters={setFilters} financeMap={financeMap} /> }] : []),
      ...(hasSlide3Selection ? [{ id: "slide-4", element: <Slide4Days filters={filters} setFilters={setFilters} financeMap={financeMap} /> }] : []),
      { id: "slide-5", element: <Slide5Stats filters={filters} setFilters={setFilters} financeMap={financeMap} requestOpenTableWithModal={requestOpenTableWithModal} /> },
      { id: "slide-6", element: <Slide6TreatmentReason filters={filters} setFilters={setFilters} financeMap={financeMap} requestOpenTableWithModal={requestOpenTableWithModal} /> },
      { id: "slide-7", element: <Slide7Departments filters={filters} setFilters={setFilters} financeMap={financeMap} requestOpenTableWithModal={requestOpenTableWithModal} /> },
      { id: "slide-8", element: <Slide8Doctors filters={filters} setFilters={setFilters} financeMap={financeMap} requestOpenTableWithModal={requestOpenTableWithModal} /> },
      { id: "slide-9", element: <Slide9Mkb10 filters={filters} setFilters={setFilters} financeMap={financeMap} requestOpenTableWithModal={requestOpenTableWithModal} /> },
      { id: "slide-10", element: <Slide10DepartmentsSummary filters={filters} setFilters={setFilters} financeMap={financeMap} requestOpenTableWithModal={requestOpenTableWithModal} /> },
      ...(hasSlide10Selection ? [{ id: "slide-11", element: <Slide11DepartmentMonths filters={filters} setFilters={setFilters} financeMap={financeMap} deptMap={deptMap} /> }] : []),
      ...(hasSlide11Selection ? [{ id: "slide-12", element: <Slide12DepartmentDays filters={filters} setFilters={setFilters} financeMap={financeMap} deptMap={deptMap} /> }] : []),
      ...(hasSlide12Selection ? [{ id: "slide-13", element: <Slide13DoctorsInDepartment filters={filters} setFilters={setFilters} financeMap={financeMap} deptMap={deptMap} requestOpenTableWithModal={requestOpenTableWithModal} /> }] : []),
      ...(hasSlide13Selection ? [{ id: "slide-14", element: <Slide14DoctorsMonths filters={filters} setFilters={setFilters} financeMap={financeMap} /> }] : []),
      ...(hasSlide14Selection ? [{ id: "slide-15", element: <Slide15DoctorsDays filters={filters} setFilters={setFilters} financeMap={financeMap} /> }] : []),
    ];
    // Табличные отчёты по письму Карасайской ЦРБ (ноябрь 2026).
    slides.push(
      { id: "report-mkb10", element: <MkbReportSlide filters={filters} fetcher={reportFetch} Container={SlideContainer} /> },
      { id: "report-dept-specialty", element: <DeptSpecialtySlide filters={filters} fetcher={reportFetch} Container={SlideContainer} /> },
      { id: "report-service-kinds", element: <ServiceKindsSlide filters={filters} fetcher={reportFetch} Container={SlideContainer} /> },
      { id: "report-soi-provider", element: <SoiProviderSlide filters={filters} fetcher={reportFetch} Container={SlideContainer} /> },
    );
    // Раздел «Контроль качества медицинских карт» — 5 плиток в конец (scope=app, снапшот).
    slides.push(
      ...buildQualitySlides("app", {
        mis_id: filters.mis_id,
        year: filters.year,
        month: filters.month,
        day: filters.day,
        date_from: filters.date_from,
        date_to: filters.date_to,
      })
    );
    return slides;
  }, [filters, setFilters, financeMap, deptMap, hasSlide2Selection, hasSlide3Selection, hasSlide10Selection, hasSlide11Selection, hasSlide12Selection, hasSlide13Selection, hasSlide14Selection]);

  const pages = useMemo(() => chunkSlides(visibleSlides, layoutByPage), [visibleSlides, layoutByPage]);

  useEffect(() => {
    if (!pages.length) {
      setPageIndex(0);
      return;
    }
    setPageIndex((prev) => Math.min(prev, pages.length - 1));
  }, [pages.length]);

  useEffect(() => {
    writeLayoutByPageToStorage(layoutByPage);
  }, [layoutByPage]);

  const currentLayout = layoutByPage[pageIndex] ?? DEFAULT_LAYOUT;


  let content: React.ReactNode = null;

  if (pathname.startsWith("/dashboards/app/hospital-cases")) {
    content = <HospitalCasesTablePage />;
  } else if (pathname.startsWith("/dashboards/app/quality-cards")) {
    content = <QualityCardsPage />;
  } else if (pathname.startsWith("/dashboards/app/table")) {
    content = <TablePage />;
  } else if (roleLoading) {
    content = (
      <div style={{ padding: 16 }}>
        <h1 style={{ margin: "0 0 10px 0" }}>Дашборды — АПП </h1>
        <div>Загрузка роли…</div>
      </div>
    );
  } else if (!roleAllowed) {
    content = (
      <div style={{ padding: 16 }}>
        <h1 style={{ margin: "0 0 10px 0" }}>Дашборды — АПП </h1>
        <div style={{ color: "#b00020", whiteSpace: "pre-wrap" }}>
          Доступ запрещён. Нужна роль DIRECTOR или SUPERADMIN.
          {"\n"}
          Текущая роль: {userRole || "не определена"}
          {roleError ? `\nОшибка получения роли: ${roleError}` : ""}
        </div>
      </div>
    );
  } else {
    content = (
      <LayoutContext.Provider value={currentLayout}>
      <SlidePresentationContext.Provider value={{ activeSlideId, setActiveSlideId }}>
        <div style={{ minHeight: "100vh", background: "#083264", padding: 10 }}>
          <div style={{ maxWidth: 1400, margin: "0 auto" }}>
            <div style={{ background: "#0e2b4b", borderRadius: 12, border: "1px solid rgba(255,255,255,0.2)", padding: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ fontSize: 14, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
                  подразделение:
                  <MisIdDropdown
                    value={filters.mis_id ?? ""}
                    options={orgOptions}
                    onChange={(value) => {
                      const v = value.trim();
                      setRangeOpen(false);
                      setPendingTable(null);
                      setFiltersState((prev) => ({
                        mis_id: v || undefined,
                        year: prev.year,
                        finance_source_ids: undefined,
                        month: undefined,
                        day: undefined,
                        soi_id: undefined,
                        treatment_reason: null,
                        treatment_reason_label: undefined,
                        department_id: undefined,
                        performer_employee: undefined,
                        performer_employee_name: undefined,
                        mkb10_id: undefined,
                        mkb10: undefined,
                        department_id_10: undefined,
                        performer_employee_13: undefined,
                        performer_employee_13_name: undefined,
                        month_d: undefined,
                        day_d: undefined,
                        month_p: undefined,
                        day_p: undefined,
                      }));
                      setPageIndex(0);
                    }}
                  />
                </label>
                <button onClick={resetAll} style={{ height: 36, padding: "0 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.3)", background: "#083264", color: "#fff", cursor: "pointer" }}>Сбросить все</button>
                <FiltersDropdown chips={chips} onRemove={clearFilter} />
                <PeriodCalendar
                  dateFrom={filters.date_from}
                  dateTo={filters.date_to}
                  onApply={(from, to) => { setFiltersState((prev) => ({ ...prev, date_from: from, date_to: to })); setPageIndex(0); }}
                  onClear={() => setFiltersState((prev) => ({ ...prev, date_from: undefined, date_to: undefined }))}
                />
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => setPageIndex((p) => Math.max(0, p - 1))} disabled={pageIndex === 0} style={{ height: 34, borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: "#083264", color: "#fff", padding: "0 10px", cursor: "pointer" }}>Назад</button>
                  <button onClick={() => setPageIndex((p) => Math.max(0, p - 1))} disabled={pageIndex === 0} style={{ height: 34, borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: "#083264", color: "#fff", padding: "0 10px", cursor: "pointer" }}>{"<<"}</button>
                  <button onClick={() => setPageIndex((p) => Math.min((pages.length || 1) - 1, p + 1))} disabled={pageIndex >= pages.length - 1} style={{ height: 34, borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: "#083264", color: "#fff", padding: "0 10px", cursor: "pointer" }}>{">>"}</button>
                  <select value={String(currentLayout)} onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!isValidLayout(next)) return;
                    setLayoutByPage((prev) => ({ ...prev, [pageIndex]: next }));
                    setActiveSlideId(null);
                  }} style={{ height: 34, borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: "#083264", color: "#fff", padding: "0 8px" }}>
                    {PAGE_LAYOUTS.map((layout) => <option key={layout} value={layout}>{layout}</option>)}
                  </select>
                  <details style={{ position: "relative" }}>
                    <summary style={{ color: "#fff", cursor: "pointer", listStyle: "none" }}>☰</summary>
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 6px)",
                        background: "#083264",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 8,
                        padding: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        zIndex: 20000,
                        minWidth: 220,
                      }}
                    >
                      {[
                        { label: "Главная", href: "/" },
                        { label: "Dashboards АПП", href: "/dashboards/app" },
                        { label: "Dashboards КС", href: "/dashboards/hospital?place=%D0%9A%D0%A1" },
                        { label: "Охрана материнства", href: "/dashboards/mother-hospital" },
                        { label: "Dashboards ДС", href: "/dashboards/hospital?place=%D0%94%D0%A1" },
                        { label: "Ситуационный центр", href: "/dashboards/situation-center" },
                        { label: "Диспансерный учёт", href: "/dashboards/duchet" },
                        { label: "Вакцинация", href: "/dashboards/vaccination" },
                      ].map((route) => (
                        <a
                          key={route.href}
                          href={route.href}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            background: "transparent",
                            border: "none",
                            textAlign: "left",
                            color: "#fff",
                            cursor: "pointer",
                            padding: "6px 8px",
                            borderRadius: 6,
                            textDecoration: "none",
                          }}
                        >
                          {route.label}
                        </a>
                      ))}
                    </div>
                  </details>
                </div>
              </div>
              {dimsError ? <ErrorBox error={dimsError} /> : null}
              {orgsError ? <ErrorBox error={`Ошибка списка организаций: ${orgsError}`} /> : null}
              {null}
            </div>

            <div
              style={{
                height: "calc(100vh - 136px)",
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!pages.length ? (
                <div style={{ color: "#fff", textAlign: "center", paddingTop: 32 }}>
                  Нет данных
                </div>
              ) : (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {pages.map((pageSlides, idx) => {
                    const layout = layoutByPage[idx] ?? DEFAULT_LAYOUT;
                    return (
                      <div
                        key={idx}
                        style={{
                          display: idx === pageIndex ? "grid" : "none",
                          gap: 8,
                          flex: 1,
                          minHeight: 0,
                          overflow: "hidden",
                          alignItems: "stretch",
                          ...getGridTemplate(layout),
                        }}
                      >
                        {renderPageCells(pageSlides, layout)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <DateRangeModal
              open={rangeOpen}
              title={pendingTable?.title || "Период для табличной части"}
              initialFrom={defaultRange.date_from}
              initialTo={defaultRange.date_to}
              onCancel={closeTableModal}
              onApply={(dateFrom, dateTo) => {
                if (!pendingTable) return;
                const baseFilters: Filters = { mis_id: filters.mis_id, finance_source_ids: filters.finance_source_ids };
                openTableByKeyInNewTab(pendingTable.tableKey, baseFilters, {
                  ...pendingTable.extra,
                  entity_name: pendingTable.entity_name,
                  date_from: dateFrom,
                  date_to: dateTo,
                });
                closeTableModal();
              }}
            />
          </div>
        </div>
      </SlidePresentationContext.Provider>
    </LayoutContext.Provider>
    );
  }

  return (
    <>
      {globalStyle}
      {content}
    </>
  );
}



export default function DashboardsApp(): JSX.Element {
  const pathname = window.location.pathname || "/";

  // Тестовая страница: /dashboards/app/test
  if (pathname.startsWith("/dashboards/app/test")) {
    return (
      <ErrorBoundary>
        <DashboardsTestPage />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <DashboardsAppInner />
    </ErrorBoundary>
  );
}


// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
// Табличная часть стационара: /dashboards/app/hospital-cases
// Список пролеченных случаев из analytics_events_hospital_fact (потолок 5000).
// ─────────────────────────────────────────────────────────────────────────
type HcSortKey = "event_date" | "date_hospital" | "bed_days" | "bed_profile_name" | "department_name" | "mkb10_code" | "outcome_name" | "finance_name" | "patient_idn";

interface HospitalCaseRow {
  doc_id: string;
  event_date: string | null;
  date_hospital: string | null;
  date_initial: string | null;
  bed_days: number | null;
  bed_profile_name: string;
  department_name: string;
  mkb10_code: string;
  outcome_name: string;
  finance_name: string;
  patient_idn: string;
  deceased: boolean;
}

function HospitalCasesTablePage() {
  const qs = new URLSearchParams(window.location.search || "");
  const title = qs.get("title") || "Пролеченные случаи";

  const apiQuery = React.useMemo(() => {
    const p = new URLSearchParams();
    ["mis_id", "place", "year", "month", "day", "date_from", "date_to", "finance_source_id", "treatment_outcome_id", "bed_profile_id", "bed_profile_ids", "mkb10_id", "department_id"].forEach((k) => {
      const v = qs.get(k);
      if (v !== null && v !== "") p.set(k, v);
    });
    return p.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.search]);

  const [rows, setRows] = useState<HospitalCaseRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState<HcSortKey>("event_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await dashboardsFetch<{ rows: HospitalCaseRow[]; total: number; truncated?: boolean }>(`/api/v1/hospital/cases-table?${apiQuery}`);
        if (alive) { setRows(data?.rows || []); setTruncated(Boolean(data?.truncated)); }
      } catch (e: any) {
        if (alive) { setError(String(e?.message || e)); setRows([]); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiQuery]);

  const cols: { key: HcSortKey; label: string; align: "left" | "center"; num?: boolean }[] = [
    { key: "event_date", label: "Выписка", align: "center" },
    { key: "date_hospital", label: "Поступление", align: "center" },
    { key: "bed_days", label: "Койко-дни", align: "center", num: true },
    { key: "bed_profile_name", label: "Профиль койки", align: "left" },
    { key: "department_name", label: "Отделение", align: "left" },
    { key: "mkb10_code", label: "МКБ-10", align: "center" },
    { key: "outcome_name", label: "Исход", align: "center" },
    { key: "finance_name", label: "Финансирование", align: "center" },
    { key: "patient_idn", label: "ИИН пациента", align: "center" },
  ];

  const fmtDate = (iso: string | null) => (iso ? iso.slice(0, 10).split("-").reverse().join(".") : "");
  const cellText = (r: HospitalCaseRow, k: HcSortKey): string => {
    if (k === "event_date") return fmtDate(r.event_date);
    if (k === "date_hospital") return fmtDate(r.date_hospital);
    if (k === "bed_days") return r.bed_days != null ? String(r.bed_days) : "";
    if (k === "outcome_name") return r.deceased && !r.outcome_name ? "Смерть" : r.outcome_name;
    return String((r as any)[k] ?? "");
  };

  const sorted = React.useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let av: any, bv: any;
      if (sortKey === "bed_days") { av = a.bed_days ?? -Infinity; bv = b.bed_days ?? -Infinity; }
      else if (sortKey === "event_date" || sortKey === "date_hospital") { av = (a as any)[sortKey] || ""; bv = (b as any)[sortKey] || ""; }
      else { av = cellText(a, sortKey).toLowerCase(); bv = cellText(b, sortKey).toLowerCase(); }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir]);

  const onSort = (k: HcSortKey) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "bed_days" ? "desc" : "asc"); }
  };

  const downloadCsv = () => {
    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const header = ["№", ...cols.map((c) => c.label)].map(esc).join(";");
    const lines = sorted.map((r, i) => [String(i + 1), ...cols.map((c) => cellText(r, c.key))].map(esc).join(";"));
    const csv = "\ufeff" + [header, ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `hospital_cases_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const page = QC_COLORS.page, card = QC_COLORS.card, border = QC_COLORS.border;
  const headerBg = "#0a3d66";
  const altRow = "rgba(72,177,204,0.06)";

  return (
    <div style={{ minHeight: "100vh", background: page, color: "#fff", padding: 16 }}>
      <div style={{ maxWidth: 1500, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>
          <div style={{ fontSize: 13, color: QC_COLORS.muted }}>{loading ? "загрузка…" : `случаев: ${sorted.length}${truncated ? " (показаны самые свежие — сузьте период)" : ""}`}</div>
          <button onClick={downloadCsv} disabled={!sorted.length} style={{ marginLeft: "auto", height: 34, padding: "0 14px", borderRadius: 8, border: "none", background: "#48B1CC", color: "#062033", fontWeight: 700, cursor: "pointer", opacity: sorted.length ? 1 : 0.5 }}>Скачать CSV</button>
        </div>

        {error ? <div style={{ color: "#ffb4b4", padding: 12 }}>{error}</div> : null}

        <div style={{ maxHeight: "calc(100vh - 96px)", overflowY: "auto", overflowX: "auto", borderRadius: 12, border: `1px solid ${border}` }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: card, color: "#fff", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ border: `1px solid ${border}`, padding: "9px 10px", background: headerBg, position: "sticky", top: 0 }}>№</th>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    title="Сортировать"
                    style={{ border: `1px solid ${border}`, padding: "9px 10px", background: headerBg, textAlign: c.align, fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer", position: "sticky", top: 0, userSelect: "none" }}
                  >
                    {c.label}{sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={r.doc_id + i} style={{ background: i % 2 === 0 ? card : altRow }}>
                  <td style={{ border: `1px solid ${border}`, padding: "8px 10px", textAlign: "center", color: QC_COLORS.muted }}>{i + 1}</td>
                  {cols.map((c) => (
                    <td key={c.key} style={{ border: `1px solid ${border}`, padding: "8px 10px", textAlign: c.align, whiteSpace: c.key === "bed_profile_name" || c.key === "department_name" ? "normal" : "nowrap" }}>
                      {cellText(r, c.key)}
                    </td>
                  ))}
                </tr>
              ))}
              {!loading && !sorted.length ? (
                <tr><td colSpan={cols.length + 1} style={{ padding: 16, textAlign: "center", color: QC_COLORS.muted }}>За выбранный период данные отсутствуют</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Табличная часть QC (drill-down): /dashboards/quality/cards
// Список карт под кликнутым виджетом. Сортировка по клику на заголовки, CSV,
// клик по строке -> карта в МИС. Синяя палитра (без фиолетового).
// ─────────────────────────────────────────────────────────────────────────
type QcSortKey = "date" | "history_number" | "department" | "doctor" | "mkb" | "types" | "defects" | "status" | "hours_open";

function QualityCardsPage() {
  const qs = new URLSearchParams(window.location.search || "");
  const title = qs.get("title") || "Карты";

  const apiQuery = React.useMemo(() => {
    const p = new URLSearchParams();
    ["scope", "mis_id", "place", "year", "month", "day", "date_from", "date_to", "drill", "type_label", "department_label", "bucket", "bed_profiles"].forEach((k) => {
      const v = qs.get(k);
      if (v !== null && v !== "") p.set(k, v);
    });
    return p.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.search]);

  const [rows, setRows] = useState<QcCardRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState<QcSortKey>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await dashboardsFetch<{ cards: QcCardRow[]; total: number; truncated?: boolean }>(`/api/v1/quality/cards?${apiQuery}`);
        if (alive) { setRows(data?.cards || []); setTruncated(Boolean(data?.truncated)); }
      } catch (e: any) {
        if (alive) { setError(String(e?.message || e)); setRows([]); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiQuery]);

  const cols: { key: QcSortKey; label: string; align: "left" | "center"; num?: boolean }[] = [
    { key: "date", label: "Дата", align: "center" },
    { key: "history_number", label: "№ карты", align: "center" },
    { key: "department", label: "Отделение", align: "left" },
    { key: "doctor", label: "Врач", align: "left" },
    { key: "mkb", label: "МКБ", align: "center" },
    { key: "types", label: "Тип(ы) дефекта", align: "left" },
    { key: "defects", label: "Дефектов", align: "center", num: true },
    { key: "status", label: "Статус", align: "center" },
    { key: "hours_open", label: "Часов открыт", align: "center", num: true },
  ];

  const cardId = (r: QcCardRow) => r.history_number || (r.doc_id ? r.doc_id.slice(0, 8) : "");
  const cellText = (r: QcCardRow, k: QcSortKey): string => {
    if (k === "date") return r.date ? r.date.slice(0, 10).split("-").reverse().join(".") : "";
    if (k === "history_number") return cardId(r);
    if (k === "types") return (r.types || []).join(", ");
    if (k === "status") return QC_STATUS_LABEL[r.status] || r.status;
    if (k === "defects") return String(r.defects ?? "");
    if (k === "hours_open") return r.hours_open != null ? String(r.hours_open) : "";
    return String((r as any)[k] ?? "");
  };

  const sorted = React.useMemo(() => {
    const col = cols.find((c) => c.key === sortKey);
    const arr = [...rows];
    arr.sort((a, b) => {
      let av: any, bv: any;
      if (sortKey === "defects" || sortKey === "hours_open") {
        av = (a as any)[sortKey] ?? -Infinity; bv = (b as any)[sortKey] ?? -Infinity;
      } else if (sortKey === "date") {
        av = a.date || ""; bv = b.date || "";
      } else {
        av = cellText(a, sortKey).toLowerCase(); bv = cellText(b, sortKey).toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    void col;
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir]);

  const onSort = (k: QcSortKey) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "defects" || k === "hours_open" ? "desc" : "asc"); }
  };

  const downloadCsv = () => {
    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const header = ["№", ...cols.map((c) => c.label)].map(esc).join(";");
    const lines = sorted.map((r, i) => [String(i + 1), ...cols.map((c) => cellText(r, c.key))].map(esc).join(";"));
    const csv = "﻿" + [header, ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `qc_cards_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const openMis = async (r: QcCardRow) => {
    const scope = new URLSearchParams(window.location.search || "").get("scope") || "app";
    try {
      let resp: { url?: string } | null = null;
      if (scope === "app") {
        // Поликлиника: боевой механизм дефектолога (analytics/mis-doc-url -> МИС docURLByUID)
        const auth = getAuthPayload();
        const headers: Record<string, string> = { Accept: "application/json" };
        if (auth?.access_token) headers["Authorization"] = `Bearer ${auth.access_token}`;
        if (auth?.iin) headers["X-User-IIN"] = auth.iin;
        const res = await fetch(`/api/v1/analytics/mis-doc-url?docId=${encodeURIComponent(r.doc_id)}&orgId=${encodeURIComponent(r.mis_id)}`, { headers, credentials: "omit" });
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try { const j = await res.json(); if (j?.detail) detail = String(j.detail); } catch { /* ignore */ }
          throw new Error(detail);
        }
        resp = await res.json();
      } else {
        // Стационар: docStacURLByUID через прокси дефектолога (там же креды МИС).
        // HospitalType бэкенд берёт из самой карты, docType=1 — история болезни.
        const auth = getAuthPayload();
        const headers: Record<string, string> = { Accept: "application/json" };
        if (auth?.access_token) headers["Authorization"] = `Bearer ${auth.access_token}`;
        if (auth?.iin) headers["X-User-IIN"] = auth.iin;
        const res = await fetch(`/api/v1/analytics/mis-doc-url-hospital?docId=${encodeURIComponent(r.doc_id)}&orgId=${encodeURIComponent(r.mis_id)}`, { headers, credentials: "omit" });
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try { const j = await res.json(); if (j?.detail) detail = String(j.detail); } catch { /* ignore */ }
          throw new Error(detail);
        }
        resp = await res.json();
      }
      if (resp?.url) { window.open(resp.url, "_blank", "noopener,noreferrer"); return; }
      alert("МИС не вернула ссылку на карту.");
    } catch (e: any) {
      alert(`Не удалось получить ссылку МИС: ${String(e?.message || e)}\nКарта: ${cardId(r)}`);
    }
  };

  const statusColor = (s: string) => s === "critical" ? "#eee36d" : s === "open" ? "#DC143C" : s === "fixed" ? "#48B1CC" : "rgba(255,255,255,0.6)";

  const page = QC_COLORS.page, card = QC_COLORS.card, border = QC_COLORS.border;
  const headerBg = "#0a3d66";
  const altRow = "rgba(72,177,204,0.06)";

  return (
    <div style={{ minHeight: "100vh", background: page, color: "#fff", padding: 16 }}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>
          <div style={{ fontSize: 13, color: QC_COLORS.muted }}>{loading ? "загрузка…" : `карт: ${sorted.length}${truncated ? " (показаны самые свежие — сузьте период)" : ""}`}</div>
          <button onClick={downloadCsv} disabled={!sorted.length} style={{ marginLeft: "auto", height: 34, padding: "0 14px", borderRadius: 8, border: "none", background: "#48B1CC", color: "#062033", fontWeight: 700, cursor: "pointer", opacity: sorted.length ? 1 : 0.5 }}>Скачать CSV</button>
        </div>

        {error ? <div style={{ color: "#ffb4b4", padding: 12 }}>{error}</div> : null}

        <div style={{ maxHeight: "calc(100vh - 96px)", overflowY: "auto", overflowX: "auto", borderRadius: 12, border: `1px solid ${border}` }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: card, color: "#fff", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ border: `1px solid ${border}`, padding: "9px 10px", background: headerBg, position: "sticky", top: 0 }}>№</th>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    title="Сортировать"
                    style={{ border: `1px solid ${border}`, padding: "9px 10px", background: headerBg, textAlign: c.align, fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer", position: "sticky", top: 0, userSelect: "none" }}
                  >
                    {c.label}{sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr
                  key={r.doc_id + i}
                  onClick={() => void openMis(r)}
                  title="Открыть карту в МИС"
                  style={{ background: i % 2 === 0 ? card : altRow, cursor: "pointer" }}
                >
                  <td style={{ border: `1px solid ${border}`, padding: "8px 10px", textAlign: "center", color: QC_COLORS.muted }}>{i + 1}</td>
                  {cols.map((c) => (
                    <td key={c.key} style={{ border: `1px solid ${border}`, padding: "8px 10px", textAlign: c.align, whiteSpace: c.key === "types" || c.key === "department" ? "normal" : "nowrap", color: c.key === "status" ? statusColor(r.status) : "#fff", fontWeight: c.key === "status" ? 700 : 400 }}>
                      {cellText(r, c.key)}
                    </td>
                  ))}
                </tr>
              ))}
              {!loading && !sorted.length ? (
                <tr><td colSpan={cols.length + 1} style={{ padding: 16, textAlign: "center", color: QC_COLORS.muted }}>За выбранный период данные отсутствуют</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
